import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore, errText } from '@/store/useStore';
import {
  Trash2, X, FileText, Info, Users, MessageSquare, History as HistoryIcon,
  ClipboardCheck, Pencil, MoreVertical, Check, ExternalLink, Phone, Paperclip, Send, Plus, Save, ArrowLeft, Eye, EyeOff, Lock,
  ChevronLeft, ChevronRight, Download, Copy, RotateCcw, RotateCw, ZoomIn, ZoomOut,
} from 'lucide-react';
import {
  SCREEN_TYPE_LABEL, DOC_CATEGORY_LABEL, EVENT_PLACE_LABEL, EVENT_PLACE_EMOJI,
  FUNNEL_STATUS, FUNNEL_BY_KEY, FUNNEL_TO_DEAL, DEAL_TO_FUNNEL, CURRENCY_LABEL,
  requiredChecks, isFinanceVerified, dealEmoji, PARTICIPANT_AGE_OPTIONS, PARTICIPANT_AGE_SEP,
  LANGUAGE_OPTIONS, LANGUAGE_SEP,
  type Deal, type FunnelStatus, type DocCategory, type ScreenType, type EventPlace,
  type Currency, type ClientType, type PaymentReceipt, type FinanceCheckKey, type GameDuration,
} from '@/types';
import { nanoid } from 'nanoid';
import { getDealPayroll, type EmployeePayResult, getCustomAddonLabels, type CustomAddonLabel } from '@/auth/adminClient';
import { cn } from '@/utils/cn';
import { mapUrl } from '@/utils/mapLink';
import { isLightboxOpen, setLightboxOpen } from '@/utils/lightbox';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { applyAttachmentBodies } from '@/utils/attachments';
import { getToken } from '@/auth/session';
import { formatHM, formatDateShort, formatDateTimeRange, startOfDay, inBranchTz } from '@/utils/time';
import { Modal } from '@/components/ui/Modal';
import { useTzShiftMs } from '@/hooks/useTzShift';
import { useIsMobile } from '@/hooks/useIsMobile';
import { hashHue, initialsOf } from '@/utils/colors';
import { okkConfig, type OkkQuestion } from '@/data/okkForm';
import { usePerms } from '@/auth/usePerms';
import { isInputTarget } from '@/hooks/useHotkeys';
import { Combobox } from '@/components/ui/Combobox';

interface Props { id: string | null; preset?: Partial<Deal>; onClose: () => void; chrome?: 'modal' | 'page'; }

// Опция команды для выпадающих списков (минимум полей + филиал для фильтрации).
type TeamOpt = { id: string; name: string; is_archived?: boolean; branch_id?: string | null; member_ids?: string[] };

/**
 * Список команд для выпадашки в карточке игры — только команды филиала игры.
 * Крайние случаи:
 *  а) игра без филиала (branchId пустой) → показываем ВСЕ активные команды (fallback,
 *     чтобы список не оказался пустым);
 *  б) у игры уже стоит команда из другого филиала (сменили филиал / старые данные) →
 *     обязательно дописываем эту текущую команду, иначе <select value=team_id> не найдёт
 *     свою опцию и отображение сломается.
 */
/** Название филиала по id — для подписи команды, взятой из другого города. */
function branchName(id?: string | null): string {
  if (!id) return '';
  return useStore.getState().branches.find((b) => b.id === id)?.name ?? '';
}

function teamsForDeal(
  teams: TeamOpt[],
  branchId: string | null | undefined,
  currentTeamId: string | null | undefined,
  mirrorBranchId?: string | null,
): TeamOpt[] {
  const active = teams.filter((t) => !t.is_archived);
  // Игра-ивент лежит в отдельном филиале «Ивент», а проходит в городе — он записан в
  // mirror_branch_id. Раньше список команд резался строго по филиалу игры, поэтому на
  // ивенте нельзя было выбрать ту же бригаду города: приходилось заводить одноимённую
  // команду филиала «Ивент», и в утренней сводке одна бригада занимала два пункта.
  // Показываем команды обоих филиалов — своего и города, где игра реально проходит.
  const allowed = [branchId, mirrorBranchId].filter(Boolean) as string[];
  let list = allowed.length > 0 ? active.filter((t) => t.branch_id && allowed.includes(t.branch_id)) : active;
  if (currentTeamId && !list.some((t) => t.id === currentTeamId)) {
    const cur = teams.find((t) => t.id === currentTeamId);
    if (cur) list = [...list, cur];
  }
  return list;
}

type Tab = 'info' | 'people' | 'comments' | 'history' | 'okk';

const TAB_DEFS: { key: Tab; icon: typeof Info; label: string }[] = [
  { key: 'info', icon: Info, label: 'Информация' },
  { key: 'people', icon: Users, label: 'Персонал' },
  { key: 'comments', icon: MessageSquare, label: 'Комментарии' },
  { key: 'history', icon: HistoryIcon, label: 'История' },
  { key: 'okk', icon: ClipboardCheck, label: 'ОКК' },
];

// Просмотр вложения по обычному http-адресу нашего сервера: сервер отдаёт файл сырым телом
// с Content-Disposition: inline, поэтому браузер открывает PDF/картинку встроенным
// просмотрщиком, а не срывается на скачивание (как бывало с blob-URL из data_url).
// Токен уходит в query: это навигация (window.open), заголовок Authorization при ней не шлётся.
const openAttachmentView = (dealId: string, key: string) => {
  const t = getToken();
  const q = `key=${encodeURIComponent(key)}${t ? `&token=${encodeURIComponent(t)}` : ''}`;
  window.open(`/api/deals/${encodeURIComponent(dealId)}/attachment-view?${q}`, '_blank', 'noopener');
};

// Просмотр чека, ПРИШЕДШЕГО ИЗ БИТРИКСА: тела файла у нас нет, есть только ссылка CRM,
// которая открывается лишь у залогиненного в Битриксе (кассир/ведущий видит страницу входа).
// Поэтому файл забирает наш сервер и отдаёт его нам inline — тем же приёмом, что и свои
// вложения (навигация window.open, токен в query).
const openBitrixFileView = (dealId: string, key: string) => {
  const t = getToken();
  const q = `key=${encodeURIComponent(key)}${t ? `&token=${encodeURIComponent(t)}` : ''}`;
  window.open(`/api/deals/${encodeURIComponent(dealId)}/bitrix-file?${q}`, '_blank', 'noopener');
};

// Чек предоплаты/остатка. Файл из Битрикса открываем через НАШ прокси (bitrix-file), рядом
// оставляем маленькую запасную ссылку на оригинал в CRM — для тех, у кого есть доступ в
// Битрикс, на случай если прокси не смог скачать файл. Свой (из ERP) — по http-адресу
// нашего сервера (attachment-view), чтобы PDF показывался inline.
// keyPrefix — 'p' для чека предоплаты, 'r' для чека остатка (ключ = <prefix>:<id>).
// Тело своего чека всегда лежит в нашей базе (has_data) — открываем по адресу даже когда
// data_url в браузер ещё не догрузился. Заглушка — только пока непонятно, есть ли файл.
function ReceiptChip({ r, dealId, keyPrefix }: { r: PaymentReceipt; dealId: string; keyPrefix: 'p' | 'r' }) {
  const base = 'inline-flex items-center gap-1.5 px-2 py-1 ml-1 rounded-lg border border-line text-xs';
  if (r.bitrix_url) {
    return (
      <span className="inline-flex items-center">
        <a href="#" onClick={(e) => { e.preventDefault(); openBitrixFileView(dealId, `${keyPrefix}:${r.id}`); }}
          className={cn(base, 'text-accent hover:bg-surface-2')}>
          <FileText className="w-3.5 h-3.5" /> {r.filename}
        </a>
        <a href={r.bitrix_url} target="_blank" rel="noreferrer" title="Открыть оригинал в Битриксе (нужен доступ в CRM)"
          className="ml-1 p-1 rounded text-ink-3 hover:text-accent hover:bg-surface-2">
          <ExternalLink className="w-3 h-3" />
        </a>
      </span>
    );
  }
  if (!r.has_data && !r.data_url) {
    return (
      <span className={cn(base, 'text-ink-3 bg-surface-2 animate-pulse')} title="Файл загружается…">
        <FileText className="w-3.5 h-3.5" /> {r.filename}
      </span>
    );
  }
  return (
    <a href="#" onClick={(e) => { e.preventDefault(); openAttachmentView(dealId, `${keyPrefix}:${r.id}`); }}
      className={cn(base, 'text-accent hover:bg-surface-2')}>
      <FileText className="w-3.5 h-3.5" /> {r.filename}
    </a>
  );
}

const GAME_DURATIONS: GameDuration[] = ['60', '90', '120', '120+'];

// ── Обязательные поля заявки (валидация ТОЛЬКО для сделок, созданных в ERP) ──
// Сделки из Битрикса (заполнен bitrix_id) не проверяются: их поля в CRM часто пустые,
// и требовать дозаполнение при каждом сохранении сломало бы синхронизацию.
type ReqKey =
  | 'start_at' | 'funnel_status' | 'prepayment_note' | 'contact_phone' | 'game_format_id'
  | 'participant_age_text' | 'participant_count_text' | 'played_before'
  | 'source_id' | 'occasion_id' | 'language' | 'game_duration_minutes';

// Порядок = порядок полей в форме (для прокрутки к первому незаполненному).
const REQUIRED_ORDER: ReqKey[] = [
  'start_at', 'funnel_status', 'prepayment_note', 'contact_phone', 'game_format_id',
  'participant_age_text', 'participant_count_text', 'played_before',
  'source_id', 'occasion_id', 'language', 'game_duration_minutes',
];

// «Заполнено»: тексты — непустая строка после trim; селекты — выбранное значение.
// played_before — boolean: false («не играли») это ЗАПОЛНЕНО, пусто = undefined/null.
function isRequiredFilled(d: Deal, key: ReqKey): boolean {
  const e = d.event;
  switch (key) {
    // Календарь ставит событие по start_at ?? event_date — заполненным считаем любое из них.
    // Без даты игра не видна ни в календаре, ни в срезах дашборда за период, но при этом
    // попадает в общий счётчик — цифры расходятся, и найти виновника нельзя.
    case 'start_at': return e.start_at != null || e.event_date != null;
    case 'funnel_status': return !!(d.funnel_status ?? DEAL_TO_FUNNEL[d.status]);
    case 'prepayment_note': return !!e.prepayment_note?.trim();
    case 'contact_phone': return !!d.contact_phone?.trim();
    case 'game_format_id': return !!e.game_format_id;
    case 'participant_age_text': return !!e.participant_age_text?.trim();
    case 'participant_count_text': return !!e.participant_count_text?.trim();
    case 'played_before': return e.played_before != null;
    case 'source_id': return !!d.source_id;
    case 'occasion_id': return !!e.occasion_id;
    case 'language': return !!e.language;
    case 'game_duration_minutes': return !!e.game_duration_minutes;
  }
}

// Сделка создана в ERP (не пришла из Битрикса) — признак: не заполнен bitrix_id.
const isErpDeal = (d: Deal): boolean => !d.bitrix_id;

/**
 * Время игры заполнено с ошибкой: окончание не позже начала. Обычно это ночная игра, где у
 * окончания оставили вчерашнюю дату, либо опечатка в месяце. По таким играм нельзя посчитать
 * отработанные часы (см. gameDuration в utils/workload.ts), поэтому карточка показывает
 * предупреждение, а в наших заявках ещё и не даёт сохранить.
 */
const timeBroken = (d: Deal): boolean =>
  d.event?.start_at != null && d.event?.end_at != null && d.event.end_at <= d.event.start_at;
const emptyRequiredKeys = (d: Deal): ReqKey[] => REQUIRED_ORDER.filter((k) => !isRequiredFilled(d, k));

// A3 «Порядок галочек»: строгая последовательность предоплата → постоплата → закрывающие.
// Для галочки — её «предыдущая»; у prepayment предыдущей нет (правило не применяется).
const FINANCE_PREV_KEY: Partial<Record<FinanceCheckKey, FinanceCheckKey>> = { postpayment: 'prepayment', closing: 'postpayment' };

// Роли на событии (как в эталоне): ключ → русское имя.
const ROLE_RU: Record<string, string> = {
  // vedushiy2 — ВТОРОЙ ведущий на той же игре, явный выбор при назначении состава (чат с
  // Аяужан 03.11.2026: раньше кто «Ведущий 1», а кто «Ведущий 2» для ставки определялось
  // молча по порядку добавления в список — непонятно и ненадёжно, выбирают теперь явно).
  vedushiy: 'Ведущий 1', vedushiy2: 'Ведущий 2', sovedushiy: 'Соведущий', didjey: 'Диджей', hostes: 'Хостес', voditel: 'Водитель', stajer: 'Стажёр',
  upravlyayushiy: 'Управляющий',
  host: 'Ведущий', tech: 'Диджей', admin: 'Ведущий', manager: 'Хостес', event_manager: 'Соведущий',
};
const ROLE_OPTIONS = ['vedushiy', 'vedushiy2', 'sovedushiy', 'didjey', 'hostes', 'voditel', 'stajer', 'upravlyayushiy'];
/** Должности (employees.role), которые реально выходят на игру — из них набирают состав смены. */
const STAGE_ROLES = new Set(['Ведущий', 'Соведущий', 'Диджей', 'Хостес', 'Водитель', 'Стажёр', 'Управляющий']);
const roleLabel = (r: string) => ROLE_RU[r] ?? r;

/** Минуты → «1 ч 30 мин» / «45 мин» / «2 ч» (для поля «Ожидание»). */
function fmtMinutesHM(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h} ч ${m} мин`;
  if (h) return `${h} ч`;
  return `${m} мин`;
}

function tsToDt(ts?: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function dtToTs(s?: string): number | undefined {
  if (!s) return undefined;
  const [date, time] = s.split('T');
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = (time ?? '00:00').split(':').map(Number);
  return new Date(y, mo - 1, d, h ?? 0, mi ?? 0).getTime();
}

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июня', 'июля', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const DOW_SHORT = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

// Заголовок-название: своё имя события, либо «выезд 1 час» из места+длительности.
function dealTitle(d: Deal): string {
  if (d.event.title && d.event.title.trim()) return d.event.title;
  const place = d.event.place === 'outside' ? 'выезд' : 'студия';
  const dur = d.event.duration_minutes
    ?? (d.event.start_at != null && d.event.end_at != null ? Math.round((d.event.end_at - d.event.start_at) / 60_000) : 60);
  const h = dur / 60;
  const word = h === 1 ? 'час' : (h >= 2 && h < 5) ? 'часа' : 'часов';
  const hStr = Number.isInteger(h) ? String(h) : String(h);
  return `${place} ${hStr} ${word}`;
}

export function DealEditor({ id, preset, onClose, chrome = 'modal' }: Props) {
  const {
    deals, branches, employees, teams, sources, occasions, game_formats, refusal_reasons,
    saveDeal, deleteDeal, newDeal, duplicateDeal, setModal, toast,
  } = useStore();
  const existing = id ? deals.find((x) => x.id === id) : null;
  const dperms = usePerms().deals;
  const canSeePayrollDetails = usePerms().pages.pay_rates;
  const myRole = useStore((s) => s.currentUser?.access_role);
  // «Подтверждено» — финальное закрытие сделки; ставит/снимает только управляющий
  // (а также руководитель). Остальные видят статус, но не меняют его.
  const canConfirm = myRole === 'branch_manager' || myRole === 'owner' || myRole === 'superadmin';

  // Роль «Отдел качества» (ОКК): открывает карточку, заполняет анкету ОКК и
  // комментарии (в т.ч. на закрытой карточке), но НЕ двигает и НЕ редактирует игру.
  const okkRole = dperms.okk && !dperms.edit && !dperms.create;

  // Вкладки по правам: ОКК — обзор (только чтение) + анкета ОКК + комментарии.
  // Исполнитель (линейный) видит «Обзор/Персонал/ОКК (только чтение)/Комментарии».
  // Вкладку «ОКК» открываем ему на ЧТЕНИЕ (результаты опроса клиента видны всем, кто
  // видит карточку); ЗАПОЛНЯТЬ анкету он не может — editable ниже завязан на mode==='edit'
  // (недоступен исполнителю) или okkRole (право deals.okk), так что для него она read-only.
  // Остальные — все вкладки; «История» — только у кого есть право её смотреть.
  const visibleTabs: Tab[] = okkRole
    ? ['info', 'okk', 'comments']
    : (!dperms.edit && !dperms.create && dperms.comment)
    ? ['info', 'people', 'okk', 'comments']
    : TAB_DEFS.map((t) => t.key).filter((k) => k !== 'history' || dperms.viewHistory);

  const tzShiftMs = useTzShiftMs();
  const dtIn = (ts?: number) => tsToDt(ts != null ? ts + tzShiftMs : undefined);
  const dtOut = (s?: string) => { const t = dtToTs(s); return t != null ? t - tzShiftMs : undefined; };
  const fmtDateTime = (ts: number) => new Date(ts + tzShiftMs).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  const [draft, setDraft] = useState<Deal | null>(null);
  const draftRef = useRef<Deal | null>(null);
  const [tab, setTab] = useState<Tab>('info');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmMenu, setConfirmMenu] = useState(false);
  // Валидация обязательных полей: показывать подсветку начинаем только после попытки
  // сохранить с пустыми полями. fieldRefs — ссылки на поля для прокрутки к первому пустому.
  const [showReqErrors, setShowReqErrors] = useState(false);
  // HTMLElement (не только Label): блок возраста — <div> с группой галочек, не <label>.
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  // ── Листание по соседним карточкам (как в старой ERP) ──────────────────────
  // Список соседей кладёт в стор тот экран, с которого карточку открыли (в его порядке).
  // Листаем только в просмотре: в режиме правки переход потерял бы несохранённые данные.
  const dealNavIds = useStore((s) => s.dealNavIds);
  const navigateDeal = useStore((s) => s.navigateDeal);
  const navIdx = id ? dealNavIds.indexOf(id) : -1;
  const canNav = navIdx >= 0 && dealNavIds.length > 1 && mode === 'view';
  const goNav = (dir: -1 | 1) => {
    if (!canNav) return;
    const next = dealNavIds[navIdx + dir];
    if (next) navigateDeal(next);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      // Не листаем из полей ввода (в т.ч. комментарии), при открытом меню карточки
      // и при открытом фото на весь экран — там стрелки листают сами фото.
      if (!canNav || menuOpen || confirmMenu || isLightboxOpen() || isInputTarget(e.target)) return;
      e.preventDefault();
      goNav(e.key === 'ArrowLeft' ? -1 : 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canNav, navIdx, dealNavIds, menuOpen, confirmMenu]); // eslint-disable-line

  // Закрытие карточки: если у сделки не указана дата мероприятия — она не попадёт в
  // календарь, поэтому перед закрытием переспрашиваем. Закрываем только по подтверждению.
  // Читаем draftRef.current, чтобы функция была стабильна и работала из Escape-хука.
  const requestClose = () => {
    const d = draftRef.current;
    // Календарь размещает событие по start_at ?? event_date (см. CalendarPage). Поэтому
    // предупреждаем только когда нет НИ времени начала, НИ даты мероприятия — иначе
    // событие реально попадает в календарь и предупреждение было бы ложным.
    if (d && !d.event?.event_date && !d.event?.start_at) {
      if (!confirm('Дата мероприятия не указана — сделка не появится в календаре. Всё равно закрыть?')) return;
    }
    onClose();
  };

  useEffect(() => {
    if (existing) { setDraft(structuredClone(existing)); setMode('view'); }
    else if (id == null && !draft) { void newDeal('b2c', preset).then((d) => { setDraft(d); setMode('edit'); }); }
  }, [existing?.id, id]); // eslint-disable-line

  // ── Вложения карточки (фото/PDF) ──────────────────────────────────────────
  // В общем списке сделок тел вложений нет (иначе календарь тянул бы десятки мегабайт).
  // Как только карточку открыли — тянем их в фоне, по одному, на любой вкладке: к моменту
  // перехода в «Комментарии» фото уже на месте. Закрыли карточку или пролистали на соседнюю
  // (стрелки ‹ ›) — хвост загрузки бросаем, ответы для закрытой карточки игнорируются.
  const loadDealAttachments = useStore((s) => s.loadDealAttachments);
  const cancelDealAttachments = useStore((s) => s.cancelDealAttachments);
  const attachBodies = useStore((s) => (id ? s.dealAttachments[id] : undefined));
  useEffect(() => {
    if (!id) return;
    void loadDealAttachments(id);
    return () => cancelDealAttachments();
  }, [id]); // eslint-disable-line
  // Догруженные тела подмешиваем в черновик, не затирая прочие правки пользователя.
  useEffect(() => {
    if (!attachBodies) return;
    setDraft((prev) => (prev ? applyAttachmentBodies(prev, attachBodies) : prev));
  }, [attachBodies]);

  // Если текущая вкладка недоступна роли — переключиться на первую разрешённую.
  useEffect(() => {
    if (!visibleTabs.includes(tab)) setTab(visibleTabs[0]);
  }, [visibleTabs.join(','), tab]); // eslint-disable-line

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isLightboxOpen()) requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]); // eslint-disable-line

  // A3 «Своя игра» ведущего (performer) — та же формула, что и scope на сервере.
  // ВАЖНО: эти хуки объявлены ДО раннего return (!draft) ниже — иначе число хуков
  // меняется между рендерами (draft грузится асинхронно) и React падает (белый экран).
  const currentUserId = useStore((s) => s.currentUser?.employee_id) ?? null;
  // Временный рубильник супер-админа (кнопка в профиле) — тоже хук, тоже ДО раннего
  // return (!draft) ниже, по той же причине (см. комментарий выше).
  const coreUnlocked = useStore((s) => s.settings.core_unlock?.enabled === true);
  const isOwnGame = useMemo(() => {
    if (!currentUserId || !draft) return false;
    const isResp = draft.responsible_id === currentUserId;
    const inStaff = Array.isArray(draft.staff_assignments)
      && draft.staff_assignments.some((s) => s.employee_id === currentUserId);
    const inTeam = !!draft.team_id && (teams.find((t) => t.id === draft.team_id)?.member_ids?.includes(currentUserId) ?? false);
    return isResp || inStaff || inTeam;
  }, [currentUserId, draft?.responsible_id, draft?.staff_assignments, draft?.team_id, teams]);

  if (!draft) {
    // В полноэкранном режиме не показываем модальную «Загрузку» — иначе экран моргает.
    if (chrome === 'page') return <div className="h-full bg-surface-0" />;
    return (
      <Overlay onClose={onClose}>
        <div className="p-10 text-center text-ink-3">Загрузка…</div>
      </Overlay>
    );
  }

  const set = <K extends keyof Deal>(k: K, v: Deal[K]) => setDraft({ ...draft, [k]: v });
  // Ручная правка времени/даты в карточке помечает событие как «время задано вручную»
  // (time_overridden), чтобы последующая синхра из Битрикса не перебивала эту правку.
  // Флаг ставим ТОЛЬКО при изменении start_at/end_at/event_date пользователем — не для
  // прочих полей события (место/формат/комментарии и т.п.).
  // Плюс: значение должно реально ОТЛИЧАТЬСЯ от прежнего — открыть поле и выбрать
  // то же самое время не считается ручной правкой и время за ERP не закрепляет.
  const TIME_KEYS: (keyof Deal['event'])[] = ['start_at', 'end_at', 'event_date'];
  const setEvent = <K extends keyof Deal['event']>(k: K, v: Deal['event'][K]) => {
    const lockTime = TIME_KEYS.includes(k) && draft.event[k] !== v;
    // Ручная правка НАЗВАНИЯ ставит title_overridden=true (аналог замка времени), чтобы синхра
    // из Битрикса не перебивала введённое в ERP название.
    const lockTitle = k === 'title' && draft.event[k] !== v;
    const patch: Partial<Deal['event']> = { [k]: v };
    if (lockTime) patch.time_overridden = true;
    if (lockTitle) patch.title_overridden = true;
    setDraft({ ...draft, event: { ...draft.event, ...patch } });
  };

  // Текущая воронка-стадия (из funnel_status, иначе из служебного status).
  const fstat: FunnelStatus = draft.funnel_status ?? DEAL_TO_FUNNEL[draft.status];
  const finfo = FUNNEL_BY_KEY.get(fstat)!;

  // A3 «Двойные галочки». У каждой финансовой отметки два слоя:
  //   зелёная (декларация «сделал») → фиолетовая (аудит финансиста).
  // Обе НЕОБРАТИМЫ. Когда у всех требуемых (по типу клиента) стоит фиолетовая —
  // карточка «финансово проверено» (архив, полный read-only).
  const archived = isFinanceVerified(draft);

  // Кто может ПОСТАВИТЬ зелёную по конкретной галочке.
  const canDeclareGreen = (key: FinanceCheckKey): boolean => {
    // Порядок: зелёную можно ставить, только если у предыдущей галочки уже стоит зелёная.
    const prevKey = FINANCE_PREV_KEY[key];
    if (prevKey && !draft.checks?.[prevKey]?.green) return false;
    if (key === 'closing') return dperms.declareGreen && draft.client_type === 'b2b';
    if (key === 'postpayment') return dperms.declareGreen || (myRole === 'performer' && isOwnGame);
    return dperms.declareGreen; // prepayment
  };

  // draftRef держит актуальный черновик, чтобы несколько быстрых отметок галочек
  // накапливались (а не перетирали друг друга на одном кадре рендера).
  draftRef.current = draft;
  const CHECK_LABEL: Record<FinanceCheckKey, string> = { prepayment: 'Предоплата', postpayment: 'Постоплата', closing: 'Закрывающие документы' };

  const declareGreen = async (key: FinanceCheckKey) => {
    const cur = draftRef.current;
    if (!cur) return;
    if (!canDeclareGreen(key)) { toast({ kind: 'warn', text: 'Нет прав ставить эту отметку.' }); return; }
    const prevKeyG = FINANCE_PREV_KEY[key];
    if (prevKeyG && !cur.checks?.[prevKeyG]?.green) { toast({ kind: 'warn', text: 'Сначала закройте предыдущую (Сделано).' }); return; }
    const existingSlot = (cur.checks ?? {})[key] ?? {};
    if (existingSlot.green) { toast({ kind: 'warn', text: 'Эту отметку снять нельзя — она как подпись.' }); return; }
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? '—');
    const history = [...(cur.history ?? []), { ts: Date.now(), user: author, action: 'Финансовая декларация', changes: [{ label: CHECK_LABEL[key], to: '🟢 сделано' }] }];
    const upd: Deal = { ...cur, checks: { ...(cur.checks ?? {}), [key]: { ...existingSlot, green: { by: author, at: Date.now() } } }, history };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) { await saveDeal(upd); toast({ kind: 'success', text: 'Отметка сохранена' }); }
  };

  const confirmPurple = async (key: FinanceCheckKey) => {
    const cur = draftRef.current;
    if (!cur) return;
    if (!dperms.confirmPurple) { toast({ kind: 'warn', text: 'Подтверждает только финансист.' }); return; }
    const prevKeyP = FINANCE_PREV_KEY[key];
    if (prevKeyP && !cur.checks?.[prevKeyP]?.purple) { toast({ kind: 'warn', text: 'Сначала финансист должен проверить предыдущую.' }); return; }
    const existingSlot = (cur.checks ?? {})[key] ?? {};
    if (!existingSlot.green) { toast({ kind: 'warn', text: 'Сначала должна стоять зелёная отметка.' }); return; }
    if (existingSlot.purple) { toast({ kind: 'warn', text: 'Эту отметку снять нельзя — она как подпись.' }); return; }
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? '—');
    const history = [...(cur.history ?? []), { ts: Date.now(), user: author, action: 'Финансовый аудит', changes: [{ label: CHECK_LABEL[key], to: '🟣 проверено' }] }];
    const upd: Deal = { ...cur, checks: { ...(cur.checks ?? {}), [key]: { ...existingSlot, purple: { by: author, at: Date.now() } } }, history };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) { await saveDeal(upd); toast({ kind: 'success', text: 'Отметка сохранена' }); }
  };

  // Выбор типа клиента (B2B/B2C) из карточки-«вопроса». Сохраняем СРАЗУ, а не только
  // в локальном черновике — иначе выбор пропадает при следующем открытии. Тип не входит
  // в «ядро» (coreChanged) на сервере, поэтому проходит и в замороженной/закрытой карточке
  // (решение O2: финансист может довести аудит даже у закрытой сделки).
  const setClientType = async (ct: ClientType) => {
    const cur = draftRef.current;
    if (!cur) return;
    const upd: Deal = { ...cur, client_type: ct };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) await saveDeal(upd);
  };

  // Фактическая сумма прямо из режима ПРОСМОТРА (для кассира/финансиста/управленца — у кого
  // canEditActual). Сохраняем сразу через API, БЕЗ входа в полный edit — по образцу галочек
  // и setClientType выше. В заблокированной (архив/финансово проверено) карточке не пишем.
  const saveActualAmount = async (v: number | null) => {
    const cur = draftRef.current;
    if (!cur) return;
    // Кассиру окно фактической суммы продлено до 72ч после игры (actualLocked), тогда как
    // общий locked закрывает карточку через сутки. Гейт здесь ДОЛЖЕН совпадать с тем, по
    // которому InfoView показывает поле (actualLocked) — иначе поле видно, а сохранить нельзя.
    if (actualLocked) { toast({ kind: 'warn', text: 'Карточка закрыта — фактическую сумму менять нельзя.' }); return; }
    if ((cur.actual_amount ?? null) === (v ?? null)) return; // без изменений — не дёргаем API
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? '—');
    const history = [...(cur.history ?? []), { ts: Date.now(), user: author, action: 'Фактическая сумма', changes: [{ label: 'Фактическая сумма', to: v != null ? v.toLocaleString('ru-RU') : '—' }] }];
    const upd: Deal = { ...cur, actual_amount: v, history };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) { await saveDeal(upd); toast({ kind: 'success', text: 'Сохранено' }); }
  };

  // Такси — та же механика инлайн-сохранения, что и фактическая сумма выше, но ОТДЕЛЬНОЕ
  // поле (по просьбе заказчика — иначе расход на такси терялся бы внутри факт.суммы).
  const saveTaxiAmount = async (v: number | null) => {
    const cur = draftRef.current;
    if (!cur) return;
    if (actualLocked) { toast({ kind: 'warn', text: 'Карточка закрыта — сумму такси менять нельзя.' }); return; }
    if ((cur.taxi_amount ?? null) === (v ?? null)) return;
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? '—');
    const history = [...(cur.history ?? []), { ts: Date.now(), user: author, action: 'Такси', changes: [{ label: 'Такси', to: v != null ? v.toLocaleString('ru-RU') : '—' }] }];
    const upd: Deal = { ...cur, taxi_amount: v, history };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) { await saveDeal(upd); toast({ kind: 'success', text: 'Сохранено' }); }
  };

  // Агентская продажа — редкий случай (ведущий/ответственный лично продал сделку), ставится
  // вручную той же галочкой-переключателем, что и «Такси»/факт.сумма.
  const saveAgentSale = async (v: boolean) => {
    const cur = draftRef.current;
    if (!cur) return;
    if (actualLocked) { toast({ kind: 'warn', text: 'Карточка закрыта — отметку об агентской продаже менять нельзя.' }); return; }
    if ((cur.agent_sale ?? false) === v) return;
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? '—');
    const history = [...(cur.history ?? []), { ts: Date.now(), user: author, action: 'Агентская продажа', changes: [{ label: 'Агентская продажа', to: v ? 'да' : 'нет' }] }];
    const upd: Deal = { ...cur, agent_sale: v, history };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) { await saveDeal(upd); toast({ kind: 'success', text: 'Сохранено' }); }
  };

  // Оценка качества игры (вопрос «quality» анкеты ОКК) — сохраняется сразу с главной вкладки
  // «Инфо», без захода в ОКК/edit. НЕ гейтится locked/actualLocked: анкета ОКК по своей
  // природе заполняется уже после закрытия карточки (чат с pinkpillies 02.11.2026) — тот же
  // принцип, что и у saveOkkRole выше.
  const saveOkkQuality = async (v: string) => {
    const cur = draftRef.current;
    if (!existing || !cur) return;
    const prevOkk = cur.okk ?? { answers: {} };
    if ((prevOkk.answers.quality ?? '') === v) return;
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? undefined);
    const firstFill = !prevOkk.created_at;
    const upd: Deal = {
      ...cur,
      okk: {
        ...prevOkk,
        answers: { ...prevOkk.answers, quality: v },
        created_at: prevOkk.created_at ?? Date.now(),
        created_by: prevOkk.created_by ?? author,
        updated_at: Date.now(),
        updated_by: author,
      },
      history: [...(cur.history ?? []), { ts: Date.now(), user: author, action: firstFill ? 'Заполнена анкета ОКК' : 'Обновлена анкета ОКК', changes: [] }],
    };
    draftRef.current = upd;
    setDraft(upd);
    await saveDeal(upd);
    toast({ kind: 'success', text: 'Сохранено' });
  };

  // Прикрепление/удаление ЧЕКОВ прямо из режима ПРОСМОТРА (кассир/финансист/управленец —
  // у кого canEditActual). Тот же механизм загрузки, что и в edit (fileToCompressedDataUrl),
  // но сохраняем СРАЗУ через saveDeal, без входа в полный edit. Массивы payment.*_receipts —
  // те же, что пропускает серверный белый список кассира (isCashierOnly). В locked-карточке
  // (архив/финансово проверено) чеки не трогаем, как и сумму.
  type ReceiptField = 'prepayment_receipts' | 'remainder_receipts';
  const addReceiptsView = async (field: ReceiptField, files: FileList | null) => {
    const cur = draftRef.current;
    if (!cur) return;
    // Финансист (financeOnly) может ДОГРУЖАТЬ чеки даже в закрытой (архив/сутки) карточке —
    // строго только добавление. Удаление чека, фактическая сумма и «ядро» остаются закрыты.
    if (actualLocked && !financeCanAttach) { toast({ kind: 'warn', text: 'Карточка закрыта — чеки менять нельзя.' }); return; }
    if (!files || files.length === 0) return;
    const added: PaymentReceipt[] = [];
    for (const f of Array.from(files)) {
      const { data_url, mime } = await fileToCompressedDataUrl(f);
      added.push({ id: nanoid(), filename: f.name, mime, size: f.size, data_url, uploaded_at: Date.now() });
    }
    const upd: Deal = { ...cur, payment: { ...cur.payment, [field]: [...(cur.payment[field] ?? []), ...added] } };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) {
      try {
        await saveDeal(upd);
        toast({ kind: 'success', text: 'Чек добавлен' });
      } catch {
        // Сохранение не прошло (напр. файл слишком большой) — saveDeal уже показал ошибку.
        // ОТКАТываем черновик: иначе файл остался бы висеть в карточке как «загруженный»,
        // хотя на сервер не попал — из-за этого и были жалобы «прикрепил, а потом нет».
        draftRef.current = cur;
        setDraft(cur);
      }
    }
  };
  const removeReceiptView = async (field: ReceiptField, rid: string) => {
    const cur = draftRef.current;
    if (!cur) return;
    if (actualLocked) { toast({ kind: 'warn', text: 'Карточка закрыта — чеки менять нельзя.' }); return; }
    const upd: Deal = { ...cur, payment: { ...cur.payment, [field]: (cur.payment[field] ?? []).filter((r) => r.id !== rid) } };
    draftRef.current = upd;
    setDraft(upd);
    if (existing) { await saveDeal(upd); toast({ kind: 'success', text: 'Чек удалён' }); }
  };

  // 4.3 Заморозка: спустя сутки после конца игры карточка только для чтения для всех,
  // кроме финансиста (financeOnly). 4.4 Архив (все три галочки) — для всех. Комментарии
  // при этом остаются доступны (треб. 4.5).
  // Конец игры для заморозки: время конца → время начала+длительность → а если времени
  // нет (импорт из Битрикса без времени) — конец дня мероприятия (event_date+24ч).
  const eventEnd = draft.event.end_at
    ?? (draft.event.start_at != null ? draft.event.start_at + (draft.event.duration_minutes ?? 60) * 60_000
    : (draft.event.event_date != null ? draft.event.event_date + 24 * 3600_000 : null));
  // Игра прошла больше суток назад (по времени ИЛИ по дате) — независимо от роли.
  const pastEvent = !!existing && eventEnd != null && Date.now() > eventEnd + 24 * 3600_000;
  // coreUnlocked — объявлен выше, до раннего return (!draft) (см. комментарий там).
  // Заморозка «ядра»: карточка закрыта для правки, если финансово проверена (архив) ЛИБО
  // прошло больше суток после конца игры (pastEvent) — для ВСЕХ, включая финансиста. Финансовые
  // галочки и комментарии в «ядро» не входят и остаются доступны и после суток (см. ниже).
  // ИСКЛЮЧЕНИЕ: супер-админ — аварийный доступ, для него карточка не закрывается (может
  // догрузить/поправить, если сотрудник не успел). Парная правка на сервере — api/index.ts.
  const locked = !coreUnlocked && (archived || pastEvent) && myRole !== 'superadmin';
  // Финансист (financeOnly) может ДОГРУЖАТЬ чеки/файлы даже в закрытой карточке (архив/сутки).
  // Строго только добавление: удаление чека, фактическая сумма и «ядро» игры остаются закрыты
  // (см. addReceiptsView, removeReceiptView, saveActualAmount и проп canAttachReceipts у InfoView).
  const financeCanAttach = dperms.financeOnly;
  // Доступ к ФАКТИЧЕСКОЙ СУММЕ и чекам — отдельное правило, НЕ общий замок карточки (locked).
  // Сервер эти поля и так не блокирует (actual_amount/чеки не входят в «ядро»/coreChanged),
  // замок был только тут, на клиенте. По ролям (решения Аяужан):
  //   · супер-админ — всегда (аварийный доступ);
  //   · финансист (financeOnly) — БЕССРОЧНО: ни время, ни архив («финансово проверено»,
  //     галочка «Постоплата») факт-сумму ему не закрывают (Аяужан 17.08 — полный доступ);
  //   · кассир (editActualAmount без edit/financeOnly) — 3 суток после игры (Аяужан 13.08,
  //     учли выходные); архив её закрывает;
  //   · остальные — как общий замок (сутки после игры / архив).
  const isCashier = dperms.editActualAmount && !dperms.edit && !dperms.financeOnly;
  const pastEventCashier = !!existing && eventEnd != null && Date.now() > eventEnd + 72 * 3600_000;
  const actualLocked =
    myRole === 'superadmin' || coreUnlocked ? false
    : dperms.financeOnly ? false
    : isCashier ? (archived || pastEventCashier)
    : locked;

  // Удаление комментариев живёт по СВОЕМУ правилу «сутки после закрытия» — отдельно от
  // заморозки карточки выше (она временно снята). Нельзя удалять, если карточка финансово
  // проверена ИЛИ прошло больше суток после игры. Добавление комментариев не ограничено.
  const commentsDeletable = coreUnlocked || (!archived && !pastEvent);
  const startEdit = () => {
    if (locked) { toast({ kind: 'warn', text: archived ? 'Карточка в архиве (финансово проверено) — только чтение.' : 'Карточка закрыта: прошло больше суток после игры. Доступны только комментарии.' }); return; }
    setShowReqErrors(false); // сброс подсветки при входе в редактирование
    setMode('edit'); setMenuOpen(false);
  };

  // ── Сохранение / удаление / подтверждение ──
  const save = async () => {
    if (locked) { toast({ kind: 'warn', text: 'Карточка закрыта: прошло больше суток после игры.' }); return; }
    // Обязательные поля — ТОЛЬКО для заявок, созданных в ERP (без bitrix_id). Сделки из
    // Битрикса сохраняются как раньше, даже с пустыми полями (иначе встанет синхронизация).
    if (isErpDeal(draft)) {
      const empties = emptyRequiredKeys(draft);
      if (empties.length > 0) {
        setShowReqErrors(true);
        setTab('info'); // поля живут на вкладке «Информация» — переключаемся, чтобы их было видно
        toast({ kind: 'warn', text: 'Заполните обязательные поля (отмечены звёздочкой).' });
        // Прокрутка/фокус к первому незаполненному — с задержкой, чтобы вкладка успела смонтироваться.
        setTimeout(() => {
          const el = fieldRefs.current[empties[0]];
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (el.querySelector('input, select, textarea') as HTMLElement | null)?.focus();
          }
        }, 60);
        return;
      }
    }
    // Состав смены: строку роли добавили, а человека не выбрали. Такое назначение бесполезно —
    // ни уведомить, ни начислить некому, а в карточке оно показывается как «неизвестно».
    // Проверяем ВСЕ сделки, включая битриксовые: состав — наше поле, из CRM оно не приходит,
    // поэтому «пусто, потому что так в Битриксе» тут невозможно.
    if ((draft.staff_assignments ?? []).some((a) => !a.employee_id?.trim())) {
      setTab('people');
      toast({ kind: 'warn', text: 'В составе есть роль без сотрудника — выберите человека или удалите строку.' });
      return;
    }
    // Защита от дурака по времени игры: окончание должно быть позже начала. Ночные игры
    // остаются возможны («23:00 сегодня → 01:00 завтра») — запрещаем только ход назад.
    // Проверки не было нигде — ни здесь, ни на сервере, ни при приёме из Битрикса, — и в
    // базе накопились игры с перепутанной датой окончания (чаще всего ночные, где дату
    // конца оставили вчерашней). Часы по ним посчитать нельзя (см. gameDuration).
    //
    // ЗАПРЕЩАЕМ только в НАШИХ заявках (ЕРП). У сделок из Битрикса время приходит из CRM:
    // запрет заблокировал бы им сохранение чего угодно — комментария, состава, чека — из-за
    // ошибки, которую в нашей карточке даже не исправить. Им вместо запрета показывается
    // предупреждение рядом со временем (см. timeBroken ниже), чтобы поправили у себя.
    if (isErpDeal(draft) && timeBroken(draft)) {
      setTab('info');
      toast({ kind: 'warn', text: 'Окончание игры не позже начала — проверьте дату и время. У ночной игры в окончании должна стоять дата следующего дня.' });
      return;
    }
    // Для заявок ERP дата — обязательное поле (см. REQUIRED_ORDER), сюда без неё уже не дойти.
    // Сделкам из Битрикса дату не навязываем: она приходит из CRM и бывает пустой — запрет
    // сохранения заблокировал бы работу с карточкой целиком. Для них оставляем вопрос.
    if (!isErpDeal(draft) && !draft.event?.event_date && !draft.event?.start_at) {
      if (!confirm('Дата мероприятия не указана — сделка не появится в календаре. Всё равно сохранить?')) return;
    }
    const synced: Deal = { ...draft, status: draft.funnel_status ? FUNNEL_TO_DEAL[draft.funnel_status] : draft.status };
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? undefined);
    const refs = { teams, game_formats, sources, occasions };
    let history = synced.history ?? [];
    if (existing) {
      const changes = buildChanges(existing, synced, refs, tzShiftMs);
      // Трекаем «Редактор» — кто вносит изменения (старый редактор → текущий).
      const prevEditor = existing.history?.length ? existing.history[existing.history.length - 1].user : undefined;
      if (author && author !== prevEditor) changes.unshift({ label: 'Редактор', from: prevEditor, to: author });
      if (changes.length > 0) history = [...history, { ts: Date.now(), user: author, action: 'Изменена информация об игре', changes }];
    } else {
      // Новая игра — детальная запись со всеми указанными параметрами.
      const blank = { ...synced, event: {}, amount: undefined, contact_name: '', contact_phone: undefined, team_id: null, source_id: null, funnel_status: undefined } as Deal;
      const changes = buildChanges(blank, synced, refs, tzShiftMs).filter((c) => c.label !== 'Название');
      history = [{ ts: Date.now(), user: author, action: 'Добавлена новая игра', changes }];
    }
    await saveDeal({ ...synced, history });
    // saveDeal бросает исключение при ошибке (в store есть catch с error-тостом), поэтому
    // сюда доходим только при успехе — показываем подтверждение (раньше карточка молча
    // уходила в просмотр и было непонятно, сохранилось ли).
    toast({ kind: 'success', text: 'Сохранено' });
    if (existing) { setMode('view'); } else { onClose(); }
  };
  const cancelEdit = () => {
    if (existing) { setDraft(structuredClone(existing)); setMode('view'); } else { onClose(); }
  };

  // A9: после ручной привязки к Битриксу store перезагружается и у сделки появляется
  // bitrix_id/sync_state, но локальный draft держит устаревшую копию. Подтягиваем свежую
  // версию из store в draft и draftRef, чтобы последующее «Сохранить» (saveDeal(draft))
  // не затёрло только что поставленную привязку старыми данными.
  const syncDraftFromStore = () => {
    const cur = draftRef.current;
    if (!cur) return;
    const fresh = useStore.getState().deals.find((x) => x.id === cur.id);
    if (fresh) { const clone = structuredClone(fresh); draftRef.current = clone; setDraft(clone); }
  };
  const onDelete = () => {
    if (!existing) return;
    if (!confirm('Удалить эту сделку?')) return;
    void deleteDeal(existing.id).then(onClose);
  };

  // Ивент-«зеркало»: убрать/вернуть городскую копию события (оригинал в своём филиале
  // остаётся). Меняем только флаг mirror_hidden. draftRef обновляем, чтобы соседние
  // действия видели свежий черновик.
  const mirrorBranch = draft.mirror_branch_id ? branches.find((b) => b.id === draft.mirror_branch_id) : null;
  const setMirrorHidden = async (hidden: boolean) => {
    const cur = draftRef.current ?? draft;
    if (hidden && !confirm('Убрать это событие из календаря города? В своём филиале оно останется.')) return;
    const upd: Deal = { ...cur, mirror_hidden: hidden };
    draftRef.current = upd;
    setDraft(upd);
    setMenuOpen(false);
    if (existing) await saveDeal(upd);
  };
  const toggleConfirm = async () => {
    const next = !draft.confirmed;
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? undefined);
    const history = [...(draft.history ?? []), {
      ts: Date.now(), user: author,
      action: next ? 'Игра подтверждена' : 'Подтверждение снято', changes: [],
    }];
    const upd: Deal = { ...draft, confirmed: next, confirmed_at: next ? Date.now() : undefined, history };
    setDraft(upd);
    setConfirmMenu(false);
    if (existing) await saveDeal(upd);
  };

  // Сохранение для роли «Отдел качества»: пишет анкету ОКК и комментарии напрямую,
  // минуя общий режим редактирования и заморозку (ОКК звонит клиенту после игры,
  // когда карточка уже закрыта). Двигать/править игру роль всё равно не может.
  const saveOkkRole = async () => {
    const cur = draftRef.current;
    if (!existing || !cur) return;
    const author = (useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? undefined);
    const prevOkk = cur.okk ?? { answers: {} };
    const firstFill = !prevOkk.created_at;
    const history = [...(cur.history ?? []), {
      ts: Date.now(), user: author,
      action: firstFill ? 'Заполнена анкета ОКК' : 'Обновлена анкета ОКК',
      changes: [],
    }];
    const upd: Deal = {
      ...cur,
      okk: {
        ...prevOkk,
        created_at: prevOkk.created_at ?? Date.now(),
        created_by: prevOkk.created_by ?? author,
        updated_at: Date.now(),
        updated_by: author,
      },
      history,
    };
    setDraft(upd);
    try {
      await saveDeal(upd);
      toast({ kind: 'success', text: 'Сохранено: анкета ОКК и комментарии.' });
    } catch {
      // Ошибку показывает сам saveDeal (store) — здесь НЕ дублируем тост, только откат черновика.
      setDraft(cur);
    }
  };

  const title = dealTitle(draft);

  // Шапка: дата/время + длительность слота + эмодзи.
  const headTs = draft.event.start_at ?? draft.event.event_date;
  const hd = headTs != null ? new Date(headTs + tzShiftMs) : null;
  const dateLabel = hd ? `${DOW_SHORT[hd.getDay()]}, ${hd.getDate()} ${MONTHS_SHORT[hd.getMonth()]} ${hd.getFullYear()}` : '—';
  const timeLabel = draft.event.start_at ? formatHM(draft.event.start_at + tzShiftMs) : '—:—';
  const slotMin = draft.event.start_at != null && draft.event.end_at != null
    ? Math.round((draft.event.end_at - draft.event.start_at) / 60_000)
    : draft.event.duration_minutes ?? 60;
  const durLabel = `${Math.floor(slotMin / 60)}:${String(slotMin % 60).padStart(2, '0')}`;

  const inner = (
    <>
      {/* HEADER */}
      <div className="flex items-center gap-2 px-4 h-14 flex-shrink-0">
        {chrome === 'modal'
          ? <button className="icon-btn !w-9 !h-9" onClick={requestClose} aria-label="Закрыть"><X className="w-5 h-5 text-accent" /></button>
          : <span className="w-9" />}
        <h2 className="flex-1 text-center text-lg font-bold text-ink-1 truncate px-2">{title} <span className="text-ink-3 font-semibold">(#{draft.number})</span></h2>
        {chrome === 'page' || okkRole || !dperms.edit ? <span className="w-9" /> : locked && mode === 'view' ? (
          <span className="icon-btn !w-9 !h-9 text-ink-3" title="Карточка закрыта (прошло больше суток после игры)"><Lock className="w-4 h-4" /></span>
        ) : mode === 'view' ? (
          <button className="icon-btn !w-9 !h-9" title="Редактировать" onClick={startEdit}><Pencil className="w-4 h-4 text-accent" /></button>
        ) : (
          <button className="icon-btn !w-9 !h-9" title="Сохранить" onClick={save}><Save className="w-4 h-4 text-accent" /></button>
        )}
        {chrome === 'modal' && <div className="relative">
          <button className="icon-btn !w-9 !h-9" title="Меню" onClick={() => setMenuOpen((v) => !v)}><MoreVertical className="w-4 h-4 text-ink-2" /></button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-11 z-40 menu min-w-[230px] px-1">
                {/* «Редактировать» убрали отсюда — дублировал кнопку-карандаш в шапке. */}
                {dperms.delete && draft.mirror_branch_id && !draft.mirror_hidden && (
                  <button className="menu-item rounded-lg !h-11" onClick={() => { void setMirrorHidden(true); }}><EyeOff className="w-4 h-4" /> Убрать из календаря города</button>
                )}
                {dperms.delete && draft.mirror_branch_id && draft.mirror_hidden && (
                  <button className="menu-item rounded-lg !h-11" onClick={() => { void setMirrorHidden(false); }}><Eye className="w-4 h-4" /> Вернуть в календарь города</button>
                )}
                {dperms.delete && (
                  <button className="menu-item rounded-lg !h-11 text-status-refused hover:!bg-status-refused/10" onClick={() => { setMenuOpen(false); onDelete(); }}><Trash2 className="w-4 h-4" /> Удалить</button>
                )}
                {/* Временно скрыто по просьбе — пункт «Открыть в отдельном окне». Код оставлен,
                    чтобы вернуть — раскомментировать блок ниже.
                {dperms.create && (
                  <button className="menu-item rounded-lg !h-11" onClick={async () => { setMenuOpen(false); if (existing) { const dup = await duplicateDeal(existing.id); if (dup) toast({ kind: 'success', text: 'Открыто отдельной копией' }); } }}><ExternalLink className="w-4 h-4" /> Открыть в отдельном окне</button>
                )}
                */}
              </div>
            </>
          )}
        </div>}
        {/* галочка подтверждения — только в просмотре и только у управляющего/руководителя */}
        {chrome === 'modal' && mode === 'view' && canConfirm && (
          <div className="relative">
            <button className={cn('icon-btn !w-9 !h-9 rounded-full border', draft.confirmed ? 'border-accent text-accent bg-accent-soft/40' : 'border-line text-ink-3')}
              title="Подтверждение" onClick={() => setConfirmMenu((v) => !v)}><Check className="w-4 h-4" /></button>
            {confirmMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setConfirmMenu(false)} />
                <div className="absolute right-0 top-11 z-40 menu min-w-[180px] px-1">
                  <button className="menu-item rounded-lg !h-11" onClick={toggleConfirm}>
                    <Check className={cn('w-4 h-4', draft.confirmed && 'text-accent')} /> Подтверждено
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Плашка «Подтверждено» */}
      {draft.confirmed && mode === 'view' && (
        <div className="flex justify-center pb-1">
          <span className="inline-flex items-center gap-2 pl-4 pr-2 h-8 rounded-full bg-accent text-white text-sm font-semibold">
            <Check className="w-4 h-4" /> Подтверждено
            {canConfirm && <button onClick={toggleConfirm} className="ml-1 w-5 h-5 rounded-full hover:bg-white/20 grid place-items-center" title="Снять подтверждение"><X className="w-3.5 h-3.5" /></button>}
          </span>
        </div>
      )}

      {/* Баннер заморозки (4.3) / архива (4.4) */}
      {locked && mode === 'view' && (
        <div className="flex justify-center pb-1 px-4">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-2 text-ink-2 text-xs font-medium text-center">
            <Lock className="w-3.5 h-3.5 flex-shrink-0" /> {archived
              ? 'Финансово проверено — карточка в архиве (только чтение).'
              : 'Игра прошла больше суток назад — карточка закрыта для изменений. Доступны только комментарии.'}
          </span>
        </div>
      )}

      {/* Баннер для финансиста: игра давняя — он не правит карточку, только финпроверка.
          13.07.2026: добавлено `dperms.financeOnly` — пока заморозка «сутки после игры»
          снята (см. выше), без этого условия баннер «изменения недоступны» показывался бы
          всем и врал: править-то как раз можно. ВЕРНУТЬ вместе с заморозкой 23.07.2026. */}
      {pastEvent && !locked && dperms.financeOnly && mode === 'view' && (
        <div className="flex justify-center pb-1 px-4">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-2 text-ink-2 text-xs font-medium text-center">
            <Lock className="w-3.5 h-3.5 flex-shrink-0" /> Игра прошла больше суток назад — изменения недоступны, можно только проставить финансовую проверку.
          </span>
        </div>
      )}

      {/* Ивент-зеркало: событие показано (или убрано) в календаре города */}
      {draft.mirror_branch_id && (
        <div className="flex justify-center pb-1 px-4">
          <span className={cn('inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium text-center',
            draft.mirror_hidden ? 'bg-surface-2 text-ink-3' : 'bg-accent-soft/60 text-accent')}>
            {draft.mirror_hidden
              ? <>Убрано из календаря города{mirrorBranch ? `: ${mirrorBranch.name}` : ''}</>
              : <>Также в календаре города{mirrorBranch ? `: ${mirrorBranch.name}` : ''}</>}
          </span>
        </div>
      )}

      {/* SUB-HEADER: дата/время | длительность | эмодзи */}
      <div className="flex items-center justify-between px-4 sm:px-6 pb-3 flex-shrink-0">
        <div className="flex items-center gap-6 sm:gap-10">
          <div>
            <div className="text-xs text-ink-3">{dateLabel}</div>
            <div className="text-2xl font-bold text-ink-1 tabular-nums">{timeLabel}</div>
          </div>
          <div>
            <div className="text-xs text-ink-3">Длительность</div>
            <div className="text-2xl font-bold text-ink-1 tabular-nums">{durLabel}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-2xl">
          <span title={draft.event.game_format_id ? game_formats.find((g) => g.id === draft.event.game_format_id)?.name : 'Формат'}>
            {dealEmoji(draft, game_formats)}
          </span>
          {draft.event.place === 'outside' && <span title="Выезд">🚗</span>}
          {draft.event.uses_invoice_account && <span title="Расчётный счёт">💳</span>}
          {!!draft.event.waiting_minutes && <span title="Ожидание">⏳</span>}
          {draft.event.english_hosting && <span title="Английское ведение">🇬🇧</span>}
        </div>
      </div>

      {/* Время игры заполнено с ошибкой: окончание не позже начала. По такой игре нельзя
          посчитать отработанные часы, поэтому предупреждаем прямо в карточке. У наших
          заявок сохранение вдобавок блокируется, у сделок из Битрикса — только пометка:
          исправлять надо в CRM, у нас это поле только читается. */}
      {timeBroken(draft) && (
        <div className="mx-4 sm:mx-6 mb-3 px-3 py-2 rounded-lg bg-status-refused/10 border border-status-refused/40 text-xs text-status-refused flex-shrink-0">
          <b>Время игры указано неверно:</b> окончание не позже начала.
          {' '}Отработанные часы по этой игре не считаются.
          {isErpDeal(draft)
            ? ' Поправьте дату и время окончания — у ночной игры это дата следующего дня.'
            : ' Поправьте дату и время окончания в Битриксе — у ночной игры это дата следующего дня.'}
        </div>
      )}

      {/* TABS — только разрешённые роли */}
      <div className="flex items-center border-b border-line flex-shrink-0 px-2">
        {TAB_DEFS.filter((t) => visibleTabs.includes(t.key)).map((t) => (
          <TabBtn key={t.key} icon={t.icon} label={t.label} active={tab === t.key} onClick={() => setTab(t.key)} />
        ))}
      </div>

      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {tab === 'info' && (mode === 'view'
          ? <InfoView draft={draft} finfo={finfo} fmtDateTime={fmtDateTime}
              branchCity={(() => { const b = draft.branch_id ? branches.find((x) => x.id === draft.branch_id) : null; return b ? (b.city || b.name) : null; })()}
              occasions={occasions} game_formats={game_formats} sources={sources} teams={teams} employees={employees}
              canDeclareGreen={canDeclareGreen} canConfirmPurple={dperms.confirmPurple}
              onDeclareGreen={declareGreen} onConfirmPurple={confirmPurple}
              onSetClientType={setClientType}
              canEditActual={dperms.financeOnly || dperms.edit || dperms.editActualAmount}
              actualLocked={actualLocked} canAttachReceipts={financeCanAttach} onSaveActual={saveActualAmount}
              onSaveTaxi={saveTaxiAmount} onSaveAgentSale={saveAgentSale} canSeePayrollDetails={canSeePayrollDetails}
              canSeeSalaryRow={canSeePayrollDetails || isCashier}
              onAddReceipt={addReceiptsView} onRemoveReceipt={removeReceiptView}
              canEditOkk={dperms.okk} onSaveOkkQuality={saveOkkQuality} />
          : <InfoEdit draft={draft} set={set} setEvent={setEvent} dtIn={dtIn} dtOut={dtOut}
              teams={teams} game_formats={game_formats} sources={sources}
              occasions={occasions} branches={branches} employees={employees} refusal_reasons={refusal_reasons}
              onBitrixLinked={syncDraftFromStore}
              canEditActual={dperms.financeOnly || dperms.edit || dperms.editActualAmount}
              requiredActive={isErpDeal(draft)}
              errorKeys={showReqErrors && isErpDeal(draft) ? emptyRequiredKeys(draft) : []}
              fieldRefs={fieldRefs} />)}

        {tab === 'people' && (mode === 'view'
          ? <PeopleView draft={draft} employees={employees} teams={teams} />
          : <PeopleEdit draft={draft} setDraft={setDraft} employees={employees} teams={teams} />)}

        {tab === 'comments' && <CommentsTab draft={draft} setDraft={setDraft} canDelete={commentsDeletable} />}

        {tab === 'history' && <HistoryTab draft={draft} tzShiftMs={tzShiftMs} />}

        {tab === 'okk' && <OkkTab draft={draft} setDraft={setDraft} editable={mode === 'edit' || okkRole} fmtDateTime={fmtDateTime} />}
      </div>

      {/* FOOTER (только в режиме редактирования) */}
      {mode === 'edit' && (
        <div className="flex items-center gap-2 px-5 min-h-14 pb-[env(safe-area-inset-bottom)] border-t border-line bg-surface-2/40 flex-shrink-0">
          <button className="btn-primary" onClick={save}>Сохранить</button>
          <button className="btn-ghost" onClick={cancelEdit}>Отмена</button>
        </div>
      )}
      {/* FOOTER для роли «Отдел качества»: сохранить анкету ОКК и комментарии. */}
      {okkRole && existing && (
        <div className="flex items-center gap-2 px-5 min-h-14 pb-[env(safe-area-inset-bottom)] border-t border-line bg-surface-2/40 flex-shrink-0">
          <button className="btn-primary" onClick={saveOkkRole}>Сохранить</button>
          <span className="text-xs text-ink-3">Сохраняются анкета ОКК и комментарии. Перемещение и правки игры недоступны.</span>
        </div>
      )}
    </>
  );

  // Стрелки листания по соседям — по краям окна, поверх карточки (в обоих режимах).
  const navArrows = canNav
    ? <NavArrows pos={navIdx + 1} total={dealNavIds.length} onPrev={() => goNav(-1)} onNext={() => goNav(1)} chrome={chrome} />
    : null;

  // Полноэкранный режим (Продажи → Игры): карточка слева + боковая панель действий справа.
  if (chrome === 'page') {
    return (
      <>
      {navArrows}
      <div className="h-full overflow-y-auto bg-surface-0">
        <div className="max-w-[1180px] mx-auto p-3 sm:p-5 flex flex-col lg:flex-row gap-3 lg:gap-4 items-start">
          <div className="panel w-full lg:flex-1 min-w-0 flex flex-col max-h-none lg:max-h-[calc(100vh-110px)] order-last lg:order-none">{inner}</div>
          <aside className="panel w-full lg:w-[280px] flex-shrink-0 p-4 space-y-2.5">
            <button className="btn-soft w-full !justify-center" onClick={requestClose}><ArrowLeft className="w-4 h-4" /> Назад</button>
            {mode === 'view' ? (
              <>
                {locked ? (
                  <div className="w-full rounded-lg bg-surface-2 text-ink-2 text-xs px-3 py-2 flex items-center gap-2"><Lock className="w-4 h-4 flex-shrink-0" /> {archived ? 'Карточка в архиве (финансово проверено) — только чтение.' : 'Карточка закрыта (прошло больше суток после игры). Доступны только комментарии.'}</div>
                ) : (
                  <button className="btn-primary w-full !justify-center" onClick={startEdit}><Pencil className="w-4 h-4" /> Редактировать</button>
                )}
                {dperms.delete && draft.mirror_branch_id && !draft.mirror_hidden && (
                  <button className="btn-soft w-full !justify-center" onClick={() => { void setMirrorHidden(true); }}><EyeOff className="w-4 h-4" /> Убрать из календаря города</button>
                )}
                {dperms.delete && draft.mirror_branch_id && draft.mirror_hidden && (
                  <button className="btn-soft w-full !justify-center" onClick={() => { void setMirrorHidden(false); }}><Eye className="w-4 h-4" /> Вернуть в календарь города</button>
                )}
                {dperms.delete && (
                  <button className="w-full h-10 rounded-lg font-semibold text-white bg-status-refused hover:brightness-105 flex items-center justify-center gap-2" onClick={onDelete}><Trash2 className="w-4 h-4" /> Удалить</button>
                )}
              </>
            ) : (
              <button className="btn-soft w-full !justify-center border-accent text-accent" onClick={() => setMode('view')}><Eye className="w-4 h-4" /> Просмотр</button>
            )}
          </aside>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Overlay onClose={requestClose}>{inner}</Overlay>
      {navArrows}
    </>
  );
}

// ── Листание по соседним карточкам ──
// В обычной (модальной) карточке — фиолетовые полупрозрачные боковые зоны, прижатые
// к краям карточки (ширина 640): кликается вся полоса, по центру — тонкий шеврон.
// В полноэкранном режиме (Продажи) карточка занимает почти весь экран, боковых зон нет —
// там оставляем простые шевроны по краям окна.
function NavArrows({ pos, total, onPrev, onNext, chrome }: {
  pos: number; total: number; onPrev: () => void; onNext: () => void; chrome?: 'modal' | 'page';
}) {
  const atStart = pos <= 1;
  const atEnd = pos >= total;
  const isMobile = useIsMobile();

  // На телефоне боковые зоны не помещаются (карточка занимает 92vw), поэтому там — компактные
  // круглые шевроны по обоим краям, примерно на треть меньше десктопных.
  if (isMobile) {
    const btn = 'fixed top-1/2 -translate-y-1/2 z-[60] grid place-items-center w-9 h-9 rounded-full '
      + 'bg-accent/20 text-accent active:bg-accent/35 transition-colors disabled:opacity-25';
    return (
      <>
        <button className={cn(btn, 'left-0.5')} disabled={atStart} onClick={onPrev}
          title="Предыдущая карточка" aria-label="Предыдущая карточка">
          <ChevronLeft className="w-7 h-7" strokeWidth={2.4} />
        </button>
        <button className={cn(btn, 'right-0.5')} disabled={atEnd} onClick={onNext}
          title="Следующая карточка" aria-label="Следующая карточка">
          <ChevronRight className="w-7 h-7" strokeWidth={2.4} />
        </button>
      </>
    );
  }

  if (chrome === 'page') {
    const btn = 'grid place-items-center text-accent/70 hover:text-accent transition-colors disabled:opacity-25 disabled:hover:text-accent/70 disabled:cursor-default';
    return (
      <>
        <button className={cn('fixed left-1 sm:left-4 top-1/2 -translate-y-1/2 z-[60]', btn)}
          disabled={atStart} onClick={onPrev} title="Предыдущая карточка (←)" aria-label="Предыдущая карточка">
          <ChevronLeft className="w-10 h-10 sm:w-12 sm:h-12" strokeWidth={2.4} />
        </button>
        <div className="fixed right-1 sm:right-4 top-1/2 -translate-y-1/2 z-[60] flex flex-col items-center gap-1">
          <button className={btn} disabled={atEnd} onClick={onNext} title="Следующая карточка (→)" aria-label="Следующая карточка">
            <ChevronRight className="w-10 h-10 sm:w-12 sm:h-12" strokeWidth={2.4} />
          </button>
          <span className="text-accent/70 text-[11px] tabular-nums whitespace-nowrap">{pos} из {total}</span>
        </div>
      </>
    );
  }

  // Компактные круглые кнопки по центру высоты экрана, в 40px от края карточки
  // (карточка — min(640px,92vw) шириной, min(92vh,860px) высотой, центрирована).
  const BTN = 44; // w-11
  const GAP = 40;
  const cardW = 'min(640px, 92vw)';
  const cardH = 'min(92vh, 860px)';
  const sideOffset = `calc(50% - (${cardW}) / 2 - ${BTN + GAP}px)`;
  const cardRight = `calc(50% - (${cardW}) / 2)`; // расстояние от правого края экрана до правого края карточки
  const counterTop = `calc((100vh - (${cardH})) / 2 + (${cardH}) + 16px)`; // низ карточки + 16px
  const btn = 'fixed top-1/2 -translate-y-1/2 z-[60] grid place-items-center w-11 h-11 rounded-full '
    + 'bg-accent/15 text-accent hover:bg-accent/25 active:bg-accent/35 transition-colors '
    + 'disabled:opacity-0 disabled:pointer-events-none';
  return (
    <>
      <button className={btn} style={{ left: sideOffset }}
        disabled={atStart} onClick={onPrev} title="Предыдущая карточка (←)" aria-label="Предыдущая карточка">
        <ChevronLeft className="w-6 h-6" strokeWidth={2.4} />
      </button>
      <button className={btn} style={{ right: sideOffset }}
        disabled={atEnd} onClick={onNext} title="Следующая карточка (→)" aria-label="Следующая карточка">
        <ChevronRight className="w-6 h-6" strokeWidth={2.4} />
      </button>
      <span className="fixed z-[60] text-accent/80 text-[11px] tabular-nums whitespace-nowrap"
        style={{ top: counterTop, right: cardRight }}>{pos} из {total}</span>
    </>
  );
}

// ── Оболочка-оверлей карточки (узкая высокая, как на эталоне) ──
function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-in">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      {/* Отступы сверху/снизу от края экрана — карточка не на всю высоту.
          На мобилке оставляем dvh-высоту почти во весь экран (панель браузера съедает место). */}
      <div className="relative w-full max-w-[640px] panel shadow-pop animate-pop flex flex-col h-[100dvh] sm:h-[92vh] sm:max-h-[860px]">
        {children}
      </div>
    </div>
  );
}

/**
 * Инлайн-редактор «Фактической суммы» в режиме ПРОСМОТРА (для кассира/финансиста/управленца).
 * Локальный текст, чтобы поле не «прыгало» при вводе; сохраняем по blur или Enter (Esc — отмена).
 * Внешние обновления value подхватываем, пока поле не в фокусе.
 */
function ActualAmountInline({ value, onSave }: { value: number | null; onSave: (v: number | null) => void }) {
  const [text, setText] = useState<string>(value != null ? String(value) : '');
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(value != null ? String(value) : ''); }, [value, focused]);
  const commit = () => {
    const trimmed = text.trim();
    const parsed = trimmed === '' ? null : parseFloat(trimmed);
    onSave(parsed != null && Number.isNaN(parsed) ? null : parsed);
  };
  return (
    <span className="inline-flex items-center gap-2">
      <input
        type="number"
        className="input !h-8 !w-48 !py-1 text-sm"
        value={text}
        placeholder="сколько по факту"
        onChange={(ev) => setText(ev.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); commit(); }}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') { ev.preventDefault(); (ev.target as HTMLInputElement).blur(); }
          else if (ev.key === 'Escape') { setText(value != null ? String(value) : ''); (ev.target as HTMLInputElement).blur(); }
        }}
      />
      {value == null && (
        <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">касса не подтвердила</span>
      )}
    </span>
  );
}

/**
 * Зарплата за эту игру (фаза 5 доработки расчёта зарплаты, встреча 06.10.2026). Сервер сам
 * решает, что отдать: обычному сотруднику — только его строку, финансисту/руководителю
 * (canManagePayRates) — весь состав, для сверки расхождений. Справочник ставок сюда никогда
 * не попадает — только готовые суммы и причина, если не посчиталось.
 */
function DealPayrollInfo({ dealId, canSeeAll }: { dealId: string; canSeeAll: boolean }) {
  const [rows, setRows] = useState<EmployeePayResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setRows(null); setError(null);
    getDealPayroll(dealId).then((r) => { if (!cancelled) setRows(r); }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [dealId]);

  if (error) return null; // нет прав/не загрузилось — молча не показываем блок, это не критично для просмотра карточки
  if (rows == null) return <div className="text-xs text-ink-3">Считаю…</div>;
  if (rows.length === 0) return null; // нет состава смены — считать некого

  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={`${r.employee_id}-${i}`} className="flex items-center justify-between gap-2 text-xs">
          <span className="text-ink-2">{roleLabel(r.role_key)}{r.level ? ` · ур.${r.level}` : ''}</span>
          {r.ok ? (
            <span className="font-semibold tabular-nums">
              {r.total!.toLocaleString('ru-RU')} ₸
              {r.addons && r.addons.length > 0 && (
                <span className="text-ink-3 font-normal"> (база {r.base!.toLocaleString('ru-RU')} + {r.addons.map((a) => a.label).join(', ')})</span>
              )}
            </span>
          ) : (
            <span className="text-ink-3" title={r.reason}>не посчиталось{canSeeAll && r.reason ? ` — ${r.reason}` : ''}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function TabBtn({ icon: Icon, label, active, onClick }: { icon: typeof Info; label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} title={label}
      className={cn('group relative flex-1 flex items-center justify-center h-11 border-b-2 transition-colors',
        active ? 'border-accent text-accent' : 'border-transparent text-ink-3 hover:text-ink-1')}>
      <Icon className="w-5 h-5" />
      {/* тултип при наведении: surface-3 + ink-1 инвертируются парой, читается в обеих темах */}
      <span className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-1/2 ml-3 whitespace-nowrap rounded-md bg-surface-3 text-ink-1 ring-1 ring-line text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
        {label}
      </span>
    </button>
  );
}

// ── ВКЛАДКА «ИНФОРМАЦИЯ» — просмотр ─────────────────────
function InfoView({ draft, finfo, fmtDateTime, branchCity, occasions, game_formats, sources, teams, employees, canDeclareGreen, canConfirmPurple, onDeclareGreen, onConfirmPurple, onSetClientType, canEditActual, actualLocked, canAttachReceipts, onSaveActual, onSaveTaxi, onSaveAgentSale, onAddReceipt, onRemoveReceipt, canSeePayrollDetails, canSeeSalaryRow, canEditOkk, onSaveOkkQuality }: {
  draft: Deal; finfo: { label: string; color: string }; fmtDateTime: (ts: number) => string;
  /** Город филиала игры — добавляется к текстовому адресу при поиске на карте. */
  branchCity: string | null;
  occasions: { id: string; name: string }[]; game_formats: { id: string; name: string }[];
  sources: { id: string; name: string }[]; teams: { id: string; name: string; color: string; icon?: string }[];
  employees: { id: string; full_name: string }[];
  canDeclareGreen: (key: FinanceCheckKey) => boolean; canConfirmPurple: boolean;
  onDeclareGreen: (key: FinanceCheckKey) => void; onConfirmPurple: (key: FinanceCheckKey) => void;
  onSetClientType: (t: ClientType) => void;
  /** Может ли редактировать «Фактическую сумму» прямо в просмотре (кассир/финансист/управленец). */
  canEditActual: boolean;
  /** Карточка заблокирована (архив/финансово проверено) — сумму не редактируем. */
  actualLocked: boolean;
  /** Финансист может ДОГРУЖАТЬ чеки даже в закрытой карточке (только добавление, без удаления). */
  canAttachReceipts: boolean;
  /** Сохранить фактическую сумму сразу (без входа в полный edit). */
  onSaveActual: (v: number | null) => void;
  /** Сохранить сумму такси сразу (без входа в полный edit). */
  onSaveTaxi: (v: number | null) => void;
  /** Включить/выключить «Агентская продажа» (5% ответственному) сразу, без входа в полный edit. */
  onSaveAgentSale: (v: boolean) => void;
  /** Видит весь состав смены и причину «не посчиталось» (финансист/руководитель/супер-админ). */
  canSeePayrollDetails: boolean;
  /** Видит строку «Зарплата» в карточке (руководитель/финансист/кассир — чат 02.11.2026). */
  canSeeSalaryRow: boolean;
  /** Прикрепить чек(и) прямо из просмотра (сохраняется сразу). */
  onAddReceipt: (field: 'prepayment_receipts' | 'remainder_receipts', files: FileList | null) => void;
  /** Удалить чек прямо из просмотра (сохраняется сразу). */
  onRemoveReceipt: (field: 'prepayment_receipts' | 'remainder_receipts', id: string) => void;
  /** Есть доступ к анкете ОКК (роль «Отдел качества» или полный доступ) — можно проставить оценку прямо отсюда. */
  canEditOkk: boolean;
  /** Сохранить оценку ОКК (1-10) сразу, без захода во вкладку «ОКК». */
  onSaveOkkQuality: (v: string) => void;
}) {
  const e = draft.event;
  const fmtName = e.game_format_id ? game_formats.find((g) => g.id === e.game_format_id)?.name : null;
  const srcName = draft.source_id ? sources.find((s) => s.id === draft.source_id)?.name : null;
  const occText = e.occasion_details || (e.occasion_id ? occasions.find((o) => o.id === e.occasion_id)?.name : null);
  const team = draft.team_id ? teams.find((t) => t.id === draft.team_id) : null;
  const resp = (draft.responsible_id ? employees.find((m) => m.id === draft.responsible_id)?.full_name : null) ?? draft.manager_name ?? null;
  const ageText = e.participant_age_text || (e.participant_age_min && e.participant_age_max ? `${e.participant_age_min}-${e.participant_age_max} лет` : null);
  const countText = e.participant_count_text || (e.participant_count != null ? String(e.participant_count) : null);

  const Row = ({ k, children }: { k: string; children: React.ReactNode }) => (
    <div className="text-sm leading-relaxed"><span className="font-bold text-ink-1">{k}:</span> <span className="text-ink-2">{children}</span></div>
  );

  return (
    <div className="space-y-4">
      {/* 3 плашки */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="flex items-center justify-center gap-1.5 h-10 rounded-lg border border-line text-sm"><span className="text-ink-3 font-bold text-xs">ID</span> <span className="font-semibold tabular-nums">{draft.number}</span></div>
        <div className="flex items-center justify-center gap-1.5 h-10 rounded-lg border border-line text-sm"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: finfo.color }} /> <span className="font-medium">{finfo.label}</span></div>
        {/* Деньги наверху — САМАЯ ТОЧНАЯ цифра на текущий момент (решение заказчика 23.07):
            касса подтвердила → фактическая сумма; ещё нет → сумма менеджера с пометкой,
            что она предварительная. Пустого места наверху не бывает никогда. */}
        <div className="flex flex-col items-center justify-center min-h-10 py-1 rounded-lg border border-line text-sm">
          <div className="flex items-center gap-1.5">
            <span className="text-ink-3">{CURRENCY_LABEL[draft.currency]}</span>
            <span className="font-bold tabular-nums">
              {(() => {
                const v = draft.actual_amount ?? draft.amount;
                return v != null ? v.toLocaleString('ru-RU') : '—';
              })()}
            </span>
          </div>
          {draft.actual_amount == null && draft.amount != null && (
            <span className="text-[9px] uppercase tracking-wider text-amber-600 leading-none mt-0.5">касса не подтвердила</span>
          )}
        </div>
      </div>

      {/* Оценка качества игры из анкеты ОКК — выводим на главную страницу карточки, чтобы её
          не нужно было искать во вкладке «ОКК» (чат с pinkpillies 02.11.2026). У кого есть
          доступ к ОКК — можно сразу проставить/поменять оценку отсюда же, без захода в анкету. */}
      {(canEditOkk || draft.okk?.answers?.quality) && (
        <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-surface-2 border border-line">
          <span>⭐</span>
          <span className="font-semibold text-ink-1">Оценка ОКК:</span>
          {canEditOkk ? (
            <select className="input !h-8 !w-20 !py-1 text-sm" value={draft.okk?.answers?.quality ?? ''}
              onChange={(ev) => onSaveOkkQuality(ev.target.value)}>
              <option value="">—</option>
              {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          ) : (
            <span className="font-bold tabular-nums">{draft.okk?.answers?.quality}/10</span>
          )}
        </div>
      )}

      {/* Привязка к Битриксу — та же метка, что и в режиме редактирования (BitrixLink),
          но только статус: привязать можно лишь через «Редактировать». Если привязана —
          клик открывает саму сделку в Битриксе в новой вкладке. */}
      {draft.bitrix_id ? (
        <a href={`https://showtoday.bitrix24.kz/crm/deal/details/${draft.bitrix_id}/`}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-accent px-3 py-2 rounded-lg bg-surface-2 border border-line hover:bg-surface-3 hover:border-accent/40 transition-colors">
          <span>🔗 Привязано к сделке #{draft.bitrix_id}</span>
          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 ml-auto" />
        </a>
      ) : (
        <div className="flex items-center gap-2 text-sm text-ink-3 px-3 py-2 rounded-lg bg-surface-2 border border-line">
          Не привязано к сделке Битрикса (заведено в ERP)
        </div>
      )}

      {/* Комментарии */}
      {e.comments && (
        <div>
          <div className="flex items-center gap-2 text-accent font-semibold text-sm mb-1"><MessageSquare className="w-4 h-4" /> Комментарии:</div>
          <div className="text-sm text-ink-2 whitespace-pre-wrap leading-relaxed">{e.comments}</div>
        </div>
      )}

      {/* Комментарий к заказу (заметка менеджера из CRM — «взять удлинитель» и т.п.) */}
      {e.order_comment && (
        <div>
          <div className="flex items-center gap-2 text-accent font-semibold text-sm mb-1"><MessageSquare className="w-4 h-4" /> Комментарий к заказу:</div>
          <div className="text-sm text-ink-2 whitespace-pre-wrap leading-relaxed">{e.order_comment}</div>
        </div>
      )}

      {/* Контакты */}
      <div>
        <div className="flex items-center gap-2 text-accent font-semibold text-sm mb-1.5"><Users className="w-4 h-4" /> Контакты:</div>
        <a href={`tel:${draft.contact_phone ?? ''}`} className="inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-line text-sm hover:bg-surface-2 transition-colors">
          <Phone className="w-3.5 h-3.5 text-ink-3" /> <span className="text-ink-1">{draft.contact_name}</span> : <span className="text-accent">{draft.contact_phone || '—'}</span>
        </a>
      </div>

      {/* Параметры */}
      <div>
        <div className="flex items-center gap-2 text-accent font-semibold text-sm mb-1.5"><Info className="w-4 h-4" /> Параметры:</div>
        <div className="space-y-0.5">
          <Row k="Адрес">{e.address || '—'}{e.address && <> <a href={mapUrl(e.address, e.geo, branchCity)} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">открыть на карте</a></>}</Row>
          <Row k="Место проведения">{e.place ? `${EVENT_PLACE_EMOJI[e.place]} ${EVENT_PLACE_LABEL[e.place]}` : '—'}</Row>
          <Row k="Расчётный счёт">{e.uses_invoice_account ? '💳 Да' : 'нет'}</Row>
          <Row k="Ожидание">{e.waiting_minutes ? `⏳ ${fmtMinutesHM(e.waiting_minutes)}` : ''}</Row>
          <Row k="Английское ведение">{e.english_hosting ? '🇬🇧 Да' : 'нет'}</Row>
          <Row k="Формат игры">{fmtName ?? '—'}</Row>
          <Row k="Играли ранее">{e.played_before ? 'да' : 'нет'}</Row>
          <Row k="Язык">{e.language ?? '—'}</Row>
          <Row k="Предоплата">{e.prepayment_note ?? (draft.payment.prepayment_amount?.toLocaleString('ru-RU') ?? '—')}</Row>
          <Row k="Остаток">{draft.payment.remainder_amount != null ? draft.payment.remainder_amount.toLocaleString('ru-RU') : '—'}</Row>
          {/* Сумма менеджера — общая сумма сделки из Битрикса (план менеджера), read-only.
              Стоит прямо над «Фактической», чтобы финансист сравнивал план с фактом кассы. */}
          <Row k="Сумма менеджера">{draft.amount != null ? `${draft.amount.toLocaleString('ru-RU')} ${CURRENCY_LABEL[draft.currency]}` : '—'}</Row>
          {/* Фактическая сумма — вписывает касса в ЕРП после игры (в Битрикс не уходит).
              Для кассира/финансиста/управленца (canEditActual) редактируется прямо в просмотре
              инлайн-инпутом с сохранением по blur; заблокированную карточку не трогаем.
              Иначе — просто значение + бейдж «касса не подтвердила». */}
          <Row k="Фактическая сумма">
            {canEditActual && !actualLocked ? (
              <>
                <ActualAmountInline value={draft.actual_amount ?? null} onSave={onSaveActual} />
                
              </>
            ) : (
              <>
                {draft.actual_amount != null ? draft.actual_amount.toLocaleString('ru-RU') : '—'}
                
                {draft.actual_amount == null && (
                  <span className="ml-2 inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">касса не подтвердила</span>
                )}
              </>
            )}
          </Row>
          {/* Такси — ОТДЕЛЬНАЯ сумма от факт.суммы (по просьбе заказчика), та же механика
              инлайн-редактирования для кассира/финансиста/управленца. */}
          <Row k="Такси">
            {canEditActual && !actualLocked ? (
              <ActualAmountInline value={draft.taxi_amount ?? null} onSave={onSaveTaxi} />
            ) : (
              draft.taxi_amount != null ? draft.taxi_amount.toLocaleString('ru-RU') : '—'
            )}
          </Row>
          {/* Агентская продажа — редкий случай (ведущий/ответственный лично продал сделку),
              5% ответственному от факт.суммы. Та же видимость, что у «Зарплаты» ниже — деньги,
              рядовому сотруднику на этой же игре видеть не нужно. */}
          {canSeePayrollDetails && (
            <Row k="Агентская продажа">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.agent_sale ?? false} disabled={actualLocked}
                  onChange={(ev) => onSaveAgentSale(ev.target.checked)} />
                <span className="text-ink-2">
                  {draft.agent_sale
                    ? (draft.actual_amount != null
                        ? `да — 5% от факт.суммы = ${Math.round(draft.actual_amount * 0.05).toLocaleString('ru-RU')}`
                        : 'да — сумма появится после подтверждения кассы')
                    : ''}
                </span>
              </label>
            </Row>
          )}
          {/* Зарплата в карточке игры — руководителю/финансисту/кассиру (отмена правки
              02.11.2026: сначала убрали для всех по просьбе pinkpillies, затем решили
              оставить для этих трёх ролей). Рядовой сотрудник её здесь не видит — расчёт за
              игру/период для себя смотрит в «Персонал → Загрузка» / «Мои игры». */}
          {canSeeSalaryRow && (
            <Row k="Зарплата"><DealPayrollInfo dealId={draft.id} canSeeAll={canSeePayrollDetails} /></Row>
          )}
          <Row k="Откуда узнали">{srcName ?? '—'}</Row>
          <Row k="Возраст участников">{ageText ?? '—'}</Row>
          <Row k="Доплата за дальность">{e.extra_distance_fee ? e.extra_distance_fee.toLocaleString('ru-RU') : 'нет'}</Row>
          <Row k="Кол-во участников">{countText ?? '—'}</Row>
          <Row k="Повод">{occText ?? '—'}</Row>
          <Row k="Услуги">{e.services && e.services.length ? e.services.join(', ') : '—'}</Row>
          {/* Чеки. Для кассира/финансиста/управленца (canEditActual) в незаблокированной
              карточке — тот же ReceiptControl, что и в edit (приложить/удалить), с мгновенным
              сохранением; дублирующий read-only список при этом не показываем. Остальным (или
              в архиве) — прежние кликабельные чипы ReceiptChip только на чтение. */}
          {/* Кнопка «Приложить чек» показывается, если карточка НЕ закрыта (кассир/финансист/
              управленец) ИЛИ финансисту в ЗАКРЫТОЙ карточке (canAttachReceipts) — догрузка чека.
              Удаление чека (крестик) в закрытой карточке скрыто: canRemove только при !actualLocked. */}
          {(canEditActual && !actualLocked) || canAttachReceipts ? (
            <>
              <div className="text-sm pt-0.5"><span className="font-bold text-ink-1">Чек предоплаты:</span>
                <ReceiptControl receipts={draft.payment.prepayment_receipts ?? []} dealId={draft.id} keyPrefix="p"
                  canRemove={canEditActual && !actualLocked}
                  onAdd={(files) => onAddReceipt('prepayment_receipts', files)}
                  onRemove={(id) => onRemoveReceipt('prepayment_receipts', id)} />
              </div>
              <div className="text-sm pt-0.5"><span className="font-bold text-ink-1">Чек остатка:</span>
                <ReceiptControl receipts={draft.payment.remainder_receipts ?? []} dealId={draft.id} keyPrefix="r"
                  canRemove={canEditActual && !actualLocked}
                  onAdd={(files) => onAddReceipt('remainder_receipts', files)}
                  onRemove={(id) => onRemoveReceipt('remainder_receipts', id)} />
              </div>
            </>
          ) : (
            <>
              {(draft.payment.prepayment_receipts ?? []).length > 0 && (
                <div className="text-sm pt-0.5"><span className="font-bold text-ink-1">Чек предоплаты:</span>{' '}
                  {(draft.payment.prepayment_receipts ?? []).map((r) => <ReceiptChip key={r.id} r={r} dealId={draft.id} keyPrefix="p" />)}
                </div>
              )}
              {(draft.payment.remainder_receipts ?? []).length > 0 && (
                <div className="text-sm pt-0.5"><span className="font-bold text-ink-1">Чек остатка:</span>{' '}
                  {(draft.payment.remainder_receipts ?? []).map((r) => <ReceiptChip key={r.id} r={r} dealId={draft.id} keyPrefix="r" />)}
                </div>
              )}
            </>
          )}
          <Row k="Тип клиента">{draft.client_type === 'b2b' ? 'B2B' : draft.client_type === 'b2c' ? 'B2C' : '— не указан'}</Row>
        </div>
      </div>

      {/* A3 «Двойные галочки»: зелёная (декларация) → фиолетовая (аудит финансиста).
          Набор зависит от типа клиента: b2c = 2, b2b = 3. Все требуемые с фиолетовой = «финансово проверено». */}
      {(() => {
        const verified = isFinanceVerified(draft);
        const checks = draft.checks ?? {};
        const LBL: Record<FinanceCheckKey, string> = { prepayment: 'Предоплата', postpayment: 'Постоплата', closing: 'Закрывающие документы' };
        return (
          <div className="pt-2 border-t border-line">
            <div className="flex items-center gap-2 text-accent font-semibold text-sm mb-1.5">
              <Check className="w-4 h-4" /> Финансовые галочки:
              {verified && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">финансово проверено</span>}
            </div>

            {/* Без типа клиента неясно, 2 или 3 галочки — просим указать (работает и в заморозке). */}
            {!draft.client_type && (
              <div className="flex items-center gap-2 text-sm mb-2 rounded-lg border border-line bg-surface-2 px-2 py-1.5">
                <span className="text-ink-2">Компания или частный?</span>
                <button type="button" onClick={() => onSetClientType('b2b')} className="px-2.5 py-1 rounded-md border border-line text-ink-1 hover:bg-surface-3 text-xs font-semibold">B2B</button>
                <button type="button" onClick={() => onSetClientType('b2c')} className="px-2.5 py-1 rounded-md border border-line text-ink-1 hover:bg-surface-3 text-xs font-semibold">B2C</button>
              </div>
            )}

            {draft.client_type && (
              <div className="space-y-1.5">
                {requiredChecks(draft.client_type).map((key) => {
                  const slot = checks[key] ?? {};
                  const hasGreen = !!slot.green;
                  const hasPurple = !!slot.purple;
                  const greenClickable = !hasGreen && canDeclareGreen(key);
                  // Порядок: фиолетовую можно ставить, только если у предыдущей галочки уже стоит фиолетовая.
                  const prevKey = FINANCE_PREV_KEY[key];
                  const prevPurpleOk = !prevKey || !!checks[prevKey]?.purple;
                  const purpleClickable = hasGreen && !hasPurple && canConfirmPurple && prevPurpleOk;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <div className="flex-1 text-sm text-ink-1">{LBL[key]}</div>
                      {/* Зелёная — декларация «сделал» */}
                      <button type="button" disabled={!greenClickable} onClick={() => onDeclareGreen(key)}
                        title={hasGreen ? `Задекларировал: ${slot.green!.by}` : 'Декларация «сделал»'}
                        className={cn('flex items-center gap-1 text-xs rounded-lg px-2 h-8 border transition-colors',
                          hasGreen ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-line text-ink-3',
                          greenClickable ? 'hover:bg-surface-2 cursor-pointer' : 'cursor-default')}>
                        <span className={cn('w-4 h-4 rounded grid place-items-center border', hasGreen ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-line')}>
                          {hasGreen && <Check className="w-3 h-3" />}
                        </span>
                        Сделано{hasGreen && <Lock className="w-3 h-3 ml-0.5" />}
                      </button>
                      {/* Фиолетовая — аудит финансиста (только поверх зелёной) */}
                      <button type="button" disabled={!purpleClickable} onClick={() => onConfirmPurple(key)}
                        title={hasPurple ? `Проверил: ${slot.purple!.by}` : (hasGreen ? 'Подтвердить (финансист)' : 'Сначала зелёная')}
                        className={cn('flex items-center gap-1 text-xs rounded-lg px-2 h-8 border transition-colors',
                          hasPurple ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-line text-ink-3',
                          purpleClickable ? 'hover:bg-surface-2 cursor-pointer' : 'cursor-default')}>
                        <span className={cn('w-4 h-4 rounded grid place-items-center border', hasPurple ? 'bg-violet-500 border-violet-500 text-white' : 'border-line')}>
                          {hasPurple && <Check className="w-3 h-3" />}
                        </span>
                        Проверено{hasPurple && <Lock className="w-3 h-3 ml-0.5" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {!canConfirmPurple && <div className="text-[11px] text-ink-3 italic mt-1">Фиолетовую (аудит) ставит финансист.</div>}
          </div>
        );
      })()}

      {/* Календарь / даты */}
      <div className="space-y-1 pt-2 border-t border-line text-sm">
        <div className="flex items-center gap-2"><span className="font-bold text-ink-1">📅 Календарь:</span>
          {team ? <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: team.color }} /> {team.icon ? team.icon + ' ' : ''}{team.name}</span> : <span className="text-ink-3">— не назначена —</span>}
        </div>
        <div><span className="font-bold text-ink-1">🛒 Продано:</span> <span className="text-ink-2">{resp ?? '—'} <span className="text-ink-3">({fmtDateTime(draft.created_at)})</span></span></div>
        <div><span className="font-bold text-ink-1">➕ Создано:</span> <span className="text-ink-2">{resp ?? '—'} <span className="text-ink-3">({fmtDateTime(draft.created_at)})</span></span></div>
        <div><span className="font-bold text-ink-1">✏️ Изменено:</span> <span className="text-ink-2">{resp ?? '—'} <span className="text-ink-3">({fmtDateTime(draft.updated_at)})</span></span></div>
      </div>
    </div>
  );
}

// ── ВКЛАДКА «ИНФОРМАЦИЯ» — редактирование ──────────────
/**
 * A9 «Ручная привязка»: связать ручное ERP-событие с существующей сделкой Битрикса.
 * Если сделка уже привязана — показываем метку. Иначе — поле ввода номера сделки
 * (+ по желанию по телефону клиента) и кнопку «Привязать». В Битрикс ничего не пишем.
 */
function BitrixLink({ draft, onLinked }: { draft: Deal; onLinked?: () => void }) {
  const saveDeal = useStore((s) => s.saveDeal);
  const linkDeal = useStore((s) => s.linkDeal);
  const toast = useStore((s) => s.toast);
  const [num, setNum] = useState('');
  const [busy, setBusy] = useState(false);

  if (draft.bitrix_id) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-2 px-3 py-2 rounded-lg bg-surface-2 border border-line">
        <span>🔗 Привязано к сделке #{draft.bitrix_id}</span>
      </div>
    );
  }

  const link = async (opts: { bitrixId?: string; phone?: string }) => {
    setBusy(true);
    try {
      await saveDeal(draft); // сначала сохранить событие, чтобы у draft.id была запись в БД
      const res = await linkDeal(draft.id, opts);
      if (res.ok) {
        // Подтянуть свежую (уже привязанную) версию сделки из store в локальный draft,
        // иначе следующее «Сохранить» затрёт bitrix_id устаревшим черновиком.
        onLinked?.();
        toast({ kind: 'success', text: 'Событие привязано к сделке #' + res.bitrixId });
      } else toast({ kind: 'warn', text: res.reason || 'Не удалось привязать' });
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка привязки' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 px-3 py-2.5 rounded-lg bg-surface-2 border border-line">
      <div className="text-xs font-medium text-ink-2">Привязка к Битриксу</div>
      <div className="flex gap-1.5">
        <input
          className="input flex-1" value={num} disabled={busy}
          onChange={(ev) => setNum(ev.target.value)}
          placeholder="Номер сделки в Битриксе"
        />
        <button
          className="px-3 h-9 rounded-lg bg-accent text-white text-sm disabled:opacity-50 flex-shrink-0"
          disabled={busy || !num.replace(/\D/g, '')}
          onClick={() => link({ bitrixId: num })}
        >Привязать</button>
      </div>
      {draft.contact_phone ? (
        <button
          className="text-xs text-accent hover:underline disabled:opacity-50"
          disabled={busy}
          onClick={() => link({ phone: draft.contact_phone })}
        >Найти по телефону клиента ({draft.contact_phone})</button>
      ) : null}
    </div>
  );
}

function InfoEdit({ draft, set, setEvent, dtIn, dtOut, teams, game_formats, sources, occasions, branches, employees, refusal_reasons, onBitrixLinked, canEditActual, requiredActive, errorKeys, fieldRefs }: {
  draft: Deal;
  set: <K extends keyof Deal>(k: K, v: Deal[K]) => void;
  setEvent: <K extends keyof Deal['event']>(k: K, v: Deal['event'][K]) => void;
  dtIn: (ts?: number) => string; dtOut: (s?: string) => number | undefined;
  teams: TeamOpt[];
  game_formats: { id: string; name: string; is_active?: boolean }[];
  sources: { id: string; name: string }[];
  occasions: { id: string; name: string }[];
  branches: { id: string; name: string }[];
  employees: { id: string; full_name: string }[];
  refusal_reasons: { id: string; name: string }[];
  onBitrixLinked?: () => void;
  // Право редактировать «Фактическую сумму» (финансовая/управленческая роль). Нет права — read-only.
  canEditActual: boolean;
  // Валидация обязательных полей (только для заявок из ERP).
  requiredActive: boolean;
  errorKeys: ReqKey[];
  fieldRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
}) {
  const e = draft.event;
  const fstat: FunnelStatus = draft.funnel_status ?? DEAL_TO_FUNNEL[draft.status];

  // Пропсы для обязательного поля: звёздочка (если валидация применима), подсветка ошибки,
  // ссылка на элемент (для прокрутки к первому незаполненному при сохранении).
  const reqProps = (key: ReqKey) => ({
    required: requiredActive,
    error: errorKeys.includes(key),
    refEl: (el: HTMLElement | null) => { fieldRefs.current[key] = el; },
  });

  // ── Возраст участников: множественный выбор (как список в Битриксе) ──
  // Храним человекочит. строку в participant_age_text (совместимость с фильтром/историей/
  // импортом). Галочки восстанавливаем разбором строки по запятой: части, совпавшие с
  // вариантами PARTICIPANT_AGE_OPTIONS, — отмечены; несовпавшие (старый произвольный текст) —
  // сохраняем как «прочее», показываем, но не теряем при переключении галочек.
  const AGE_SET = new Set<string>(PARTICIPANT_AGE_OPTIONS as readonly string[]);
  const ageParts = (e.participant_age_text ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const ageSelected = new Set(ageParts.filter((s) => AGE_SET.has(s)));
  const ageExtra = ageParts.filter((s) => !AGE_SET.has(s));
  const toggleAge = (opt: string, on: boolean) => {
    const next = new Set(ageSelected);
    if (on) next.add(opt); else next.delete(opt);
    // Порядок вариантов — как в справочнике; «прочее» дописываем в конец, тем же разделителем.
    const combined = [...PARTICIPANT_AGE_OPTIONS.filter((o) => next.has(o)), ...ageExtra];
    setEvent('participant_age_text', combined.length ? combined.join(PARTICIPANT_AGE_SEP) : undefined);
  };

  // ── Язык: множественный выбор галочками (тот же приём, что и возраст) ──
  // Храним человекочит. строку в event.language через «, ». Галочки восстанавливаем
  // разбором: части, совпавшие с LANGUAGE_OPTIONS, — отмечены; несовпавшие (старый
  // произвольный текст) — «прочее», не теряем при переключении галочек.
  const LANG_SET = new Set<string>(LANGUAGE_OPTIONS as readonly string[]);
  const langParts = (e.language ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const langSelected = new Set(langParts.filter((s) => LANG_SET.has(s)));
  const langExtra = langParts.filter((s) => !LANG_SET.has(s));
  const toggleLang = (opt: string, on: boolean) => {
    const next = new Set(langSelected);
    if (on) next.add(opt); else next.delete(opt);
    const combined = [...LANGUAGE_OPTIONS.filter((o) => next.has(o)), ...langExtra];
    setEvent('language', combined.length ? combined.join(LANGUAGE_SEP) : undefined);
  };

  // Приложить чек к предоплате/остатку. Файлы сжимаем той же функцией, что и вложения
  // комментариев, и складываем новыми массивами (без мутации) через set('payment', ...).
  const addReceipts = async (field: 'prepayment_receipts' | 'remainder_receipts', files: FileList | null) => {
    if (!files || files.length === 0) return;
    const added: PaymentReceipt[] = [];
    for (const f of Array.from(files)) {
      const { data_url, mime } = await fileToCompressedDataUrl(f);
      added.push({ id: nanoid(), filename: f.name, mime, size: f.size, data_url, uploaded_at: Date.now() });
    }
    set('payment', { ...draft.payment, [field]: [...(draft.payment[field] ?? []), ...added] });
  };
  const removeReceipt = (field: 'prepayment_receipts' | 'remainder_receipts', id: string) => {
    set('payment', { ...draft.payment, [field]: (draft.payment[field] ?? []).filter((r) => r.id !== id) });
  };

  return (
    <div className="space-y-3">
      <BitrixLink draft={draft} onLinked={onBitrixLinked} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Начало" {...reqProps('start_at')}><DateTimePicker value={dtIn(e.start_at)} onChange={(v) => setEvent('start_at', dtOut(v))} /></Field>
        <Field label="Окончание"><DateTimePicker value={dtIn(e.end_at)} onChange={(v) => setEvent('end_at', dtOut(v))} /></Field>
      </div>
      <Field label="Название">
        <input className="input" value={e.title ?? ''} onChange={(ev) => setEvent('title', ev.target.value)} placeholder="Например: ТИМБИЛДИНГ 2ч+1ч ведение" />
        {e.title_overridden === true && (
          <button type="button" className="mt-1 text-xs text-ink-3 hover:text-ink-1 underline" onClick={() => setEvent('title_overridden', false)}>
            ↩ вернуть название из Битрикса
          </button>
        )}
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Field label="Статус заказа" {...reqProps('funnel_status')}>
          <select className="input" value={fstat} onChange={(ev) => set('funnel_status', ev.target.value as FunnelStatus)}>
            {FUNNEL_STATUS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </Field>
        <Field label="Команда">
          <select className="input" value={draft.team_id ?? ''} onChange={(ev) => set('team_id', ev.target.value || null)}>
            <option value="">— не назначена —</option>
            {teamsForDeal(teams, draft.branch_id, draft.team_id, draft.mirror_branch_id).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}{t.branch_id && t.branch_id !== draft.branch_id ? ` · ${branchName(t.branch_id)}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Общая сумма">
          <div className="flex gap-1.5">
            <input type="number" className="input flex-1" value={draft.amount ?? ''} onChange={(ev) => set('amount', ev.target.value ? parseFloat(ev.target.value) : undefined)} />
            <select className="input w-[88px] flex-shrink-0" value={draft.currency} onChange={(ev) => set('currency', ev.target.value as Currency)}>
              {(Object.keys(CURRENCY_LABEL) as Currency[]).map((c) => <option key={c} value={c}>{CURRENCY_LABEL[c]}</option>)}
            </select>
          </div>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Предоплата" {...reqProps('prepayment_note')}>
          <input className="input" value={e.prepayment_note ?? ''} onChange={(ev) => setEvent('prepayment_note', ev.target.value)} placeholder="50% или 225000" />
          <ReceiptControl receipts={draft.payment.prepayment_receipts ?? []} dealId={draft.id} keyPrefix="p" onAdd={(files) => addReceipts('prepayment_receipts', files)} onRemove={(id) => removeReceipt('prepayment_receipts', id)} />
        </Field>
        <Field label="Остаток">
          <input type="number" className="input" value={draft.payment.remainder_amount ?? ''} onChange={(ev) => set('payment', { ...draft.payment, remainder_amount: ev.target.value ? parseFloat(ev.target.value) : undefined })} placeholder="сумма остатка" />
          <ReceiptControl receipts={draft.payment.remainder_receipts ?? []} dealId={draft.id} keyPrefix="r" onAdd={(files) => addReceipts('remainder_receipts', files)} onRemove={(id) => removeReceipt('remainder_receipts', id)} />
        </Field>
      </div>

      {/* Сумма менеджера — общая сумма сделки из Битрикса (план менеджера), производное read-only
          значение (не отдельное поле). Показываем рядом с фактической для сравнения план/факт. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Сумма менеджера">
          <div className="flex items-center h-10 px-3 rounded-lg border border-line text-sm tabular-nums text-ink-2">
            {draft.amount != null ? `${draft.amount.toLocaleString('ru-RU')} ${CURRENCY_LABEL[draft.currency]}` : '—'}
          </div>
        </Field>
      </div>

      {/* Фактическая сумма — вписывает касса/финансист в ЕРП после игры (в Битрикс НЕ уходит).
          Право правки — только у финансовой/управленческой роли; остальным — read-only значение. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Фактическая сумма">
          {canEditActual ? (
            <input type="number" className="input" value={draft.actual_amount ?? ''} placeholder="сколько получили по факту"
              onChange={(ev) => set('actual_amount', ev.target.value ? parseFloat(ev.target.value) : null)} />
          ) : (
            <div className="flex items-center h-10 px-3 rounded-lg border border-line text-sm tabular-nums text-ink-2">
              {draft.actual_amount != null ? draft.actual_amount.toLocaleString('ru-RU') : '— касса не подтвердила'}
            </div>
          )}
        </Field>
        {/* Такси — ОТДЕЛЬНАЯ сумма от факт.суммы (по просьбе заказчика), то же право правки. */}
        <Field label="Такси">
          {canEditActual ? (
            <input type="number" className="input" value={draft.taxi_amount ?? ''} placeholder="сколько отправили на такси"
              onChange={(ev) => set('taxi_amount', ev.target.value ? parseFloat(ev.target.value) : null)} />
          ) : (
            <div className="flex items-center h-10 px-3 rounded-lg border border-line text-sm tabular-nums text-ink-2">
              {draft.taxi_amount != null ? draft.taxi_amount.toLocaleString('ru-RU') : '—'}
            </div>
          )}
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Сумма доп. услуг"><input type="number" className="input" value={e.extra_services_amount ?? ''} onChange={(ev) => setEvent('extra_services_amount', ev.target.value ? parseFloat(ev.target.value) : undefined)} /></Field>
        <Field label="Доплата за дальность"><input type="number" className="input" value={e.extra_distance_fee ?? ''} onChange={(ev) => setEvent('extra_distance_fee', ev.target.value ? parseFloat(ev.target.value) : undefined)} /></Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Телефон гостя" {...reqProps('contact_phone')}><input className="input" value={draft.contact_phone ?? ''} onChange={(ev) => set('contact_phone', ev.target.value)} /></Field>
        <Field label="Имя гостя"><input className="input" value={draft.contact_name} onChange={(ev) => set('contact_name', ev.target.value)} /></Field>
      </div>

      <Field label="Комментарий"><textarea className="input min-h-[70px] py-2 resize-none" value={e.comments ?? ''} onChange={(ev) => setEvent('comments', ev.target.value)} /></Field>

      <Field label="Адрес"><textarea className="input min-h-[54px] py-2 resize-none" value={e.address ?? ''} onChange={(ev) => setEvent('address', ev.target.value)} /></Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Место проведения">
          <select className="input" value={e.place ?? 'studio'} onChange={(ev) => setEvent('place', ev.target.value as EventPlace)}>
            {(Object.keys(EVENT_PLACE_LABEL) as EventPlace[]).map((p) => (
              <option key={p} value={p}>{EVENT_PLACE_EMOJI[p]} {EVENT_PLACE_LABEL[p]}</option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm cursor-pointer text-ink-2 pt-5">
          <input type="checkbox" className="w-4 h-4 accent-accent" checked={e.uses_invoice_account ?? false} onChange={(ev) => setEvent('uses_invoice_account', ev.target.checked)} /> Расчётный счёт
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Ожидание (доплата команде за простой)">
          <div className="flex items-center gap-2">
            <input type="number" min={0} className="input !w-20" placeholder="ч"
              value={Math.floor((e.waiting_minutes ?? 0) / 60) || ''}
              onChange={(ev) => {
                const h = Math.max(0, Number(ev.target.value) || 0);
                const m = (e.waiting_minutes ?? 0) % 60;
                setEvent('waiting_minutes', h * 60 + m || null);
              }} /> ч
            <input type="number" min={0} max={59} className="input !w-20" placeholder="мин"
              value={(e.waiting_minutes ?? 0) % 60 || ''}
              onChange={(ev) => {
                const m = Math.min(59, Math.max(0, Number(ev.target.value) || 0));
                const h = Math.floor((e.waiting_minutes ?? 0) / 60);
                setEvent('waiting_minutes', h * 60 + m || null);
              }} /> мин
          </div>
        </Field>
        <label className="flex items-center gap-2 text-sm cursor-pointer text-ink-2 pt-5">
          <input type="checkbox" className="w-4 h-4 accent-accent" checked={e.english_hosting ?? false} onChange={(ev) => setEvent('english_hosting', ev.target.checked)} /> Английское ведение (доплата ведущему)
        </label>
      </div>

      <Field label="Формат игры" {...reqProps('game_format_id')}>
        <select className="input" value={e.game_format_id ?? ''} onChange={(ev) => setEvent('game_format_id', ev.target.value || undefined)}>
          <option value="">— не выбран —</option>
          {/* Убранный в Битриксе формат не предлагаем для НОВОГО выбора, но если он уже стоит
              на этой игре — показываем его же отдельной опцией, с пометкой, иначе список
              «потеряет» текущее значение и покажет пустой выбор. */}
          {game_formats.filter((g) => g.is_active !== false).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          {(() => {
            const cur = game_formats.find((g) => g.id === e.game_format_id);
            return cur && cur.is_active === false ? <option value={cur.id}>{cur.name} (убран в Битриксе)</option> : null;
          })()}
        </select>
      </Field>

      {/* Возраст участников — множественный выбор галочками (как список в Битриксе). */}
      {(() => {
        const rp = reqProps('participant_age_text');
        return (
          <div ref={rp.refEl} className={cn('block', rp.error && 'field-error')}>
            <span className="block text-[11px] text-ink-3 mb-0.5">
              Возраст участников{rp.required && <span className="text-status-refused ml-0.5">*</span>}
            </span>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1">
              {PARTICIPANT_AGE_OPTIONS.map((opt) => (
                <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer text-ink-2">
                  <input type="checkbox" className="w-4 h-4 accent-accent" checked={ageSelected.has(opt)}
                    onChange={(ev) => toggleAge(opt, ev.target.checked)} /> {opt}
                </label>
              ))}
            </div>
            {ageExtra.length > 0 && (
              <span className="block text-[11px] text-ink-3 mt-1">Прочее: {ageExtra.join(', ')}</span>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Кол-во участников" {...reqProps('participant_count_text')}><input className="input" value={e.participant_count_text ?? ''} onChange={(ev) => setEvent('participant_count_text', ev.target.value)} placeholder="30-34 / 25 человек" /></Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Играли ранее" {...reqProps('played_before')}>
          {/* Пусто = не выбрано (undefined). «нет» (false) — это заполненное значение. */}
          <select className="input" value={e.played_before == null ? '' : e.played_before ? 'yes' : 'no'}
            onChange={(ev) => setEvent('played_before', ev.target.value === '' ? undefined : ev.target.value === 'yes')}>
            <option value="">— не выбрано —</option>
            <option value="no">нет</option><option value="yes">да</option>
          </select>
        </Field>
        <Field label="Откуда узнали" {...reqProps('source_id')}>
          <select className="input" value={draft.source_id ?? ''} onChange={(ev) => set('source_id', ev.target.value || null)}>
            <option value="">— не выбран —</option>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Повод" {...reqProps('occasion_id')}>
          <select className="input" value={e.occasion_id ?? ''} onChange={(ev) => setEvent('occasion_id', ev.target.value || undefined)}>
            <option value="">— не выбран —</option>
            {occasions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
        <Field label="Подробнее о поводе"><input className="input" value={e.occasion_details ?? ''} onChange={(ev) => setEvent('occasion_details', ev.target.value)} placeholder="корпоратив / тимбилдинг" /></Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* Язык — множественный выбор галочками (казах.+рус. и т.п.), как возраст. */}
        <Field label="Язык" {...reqProps('language')}>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1.5">
            {LANGUAGE_OPTIONS.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer text-ink-2">
                <input type="checkbox" className="w-4 h-4 accent-accent" checked={langSelected.has(opt)}
                  onChange={(ev) => toggleLang(opt, ev.target.checked)} /> {opt}
              </label>
            ))}
          </div>
          {langExtra.length > 0 && (
            <span className="block text-[11px] text-ink-3 mt-1">Прочее: {langExtra.join(', ')}</span>
          )}
        </Field>
        <Field label="Длительность игры (мин)" {...reqProps('game_duration_minutes')}>
          <select className="input" value={e.game_duration_minutes ?? ''} onChange={(ev) => setEvent('game_duration_minutes', ev.target.value ? (ev.target.value as GameDuration) : undefined)}>
            <option value="">—</option>
            {GAME_DURATIONS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Экран на площадке">
          <select className="input" value={e.screen_type ?? ''} onChange={(ev) => setEvent('screen_type', (ev.target.value || undefined) as ScreenType | undefined)}>
            <option value="">— не указан —</option>
            {(Object.keys(SCREEN_TYPE_LABEL) as ScreenType[]).map((s) => <option key={s} value={s}>{SCREEN_TYPE_LABEL[s]}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm cursor-pointer text-ink-2 pt-5">
          <input type="checkbox" className="w-4 h-4 accent-accent" checked={e.has_sound ?? false} onChange={(ev) => setEvent('has_sound', ev.target.checked)} /> Звук на площадке
        </label>
      </div>


      {/* «Тип сделки» (продажа/ивент = наш kind) СКРЫТ 03.07: приходит сам из воронки Битрикса
          (обычная → B2C, воронка «Ивент» → event), ручной выбор только путал. Тип КЛИЕНТА
          (B2B/B2C) — отдельное поле ниже, его оставляем. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Услуги">
          <input className="input" value={(e.services ?? []).join(', ')} onChange={(ev) => setEvent('services', ev.target.value ? ev.target.value.split(',').map((s) => s.trim()).filter(Boolean) : undefined)} placeholder="фотозона, ведущий, кейтеринг" />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Тип клиента">
          <select className="input" value={draft.client_type ?? ''} onChange={(ev) => set('client_type', (ev.target.value || undefined) as ClientType | undefined)}>
            <option value="">— не указан —</option>
            <option value="b2b">B2B</option>
            <option value="b2c">B2C</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Field label="Филиал">
          <select className="input" value={draft.branch_id ?? ''} onChange={(ev) => set('branch_id', ev.target.value || null)}>
            <option value="">— не выбран —</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <Field label="Ответственный">
          <Combobox
            value={draft.responsible_id ?? null}
            onChange={(id) => set('responsible_id', id)}
            options={employees.map((m) => ({ id: m.id, label: m.full_name }))}
            emptyLabel="— не выбран —"
          />
        </Field>
        <Field label="Взято в работу"><DateTimePicker value={dtIn(draft.taken_at)} onChange={(v) => set('taken_at', dtOut(v))} /></Field>
      </div>

      {/* Отказ */}
      <div className="pt-2 border-t border-line space-y-2">
        <div className="text-sm font-semibold text-status-refused">Отказ</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Field label="Причина отказа">
            <select className="input" value={draft.refusal?.reason_id ?? ''} onChange={(ev) => set('refusal', { ...draft.refusal, reason_id: ev.target.value || undefined })}>
              <option value="">— нет —</option>
              {refusal_reasons.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="Комментарий к отказу"><input className="input" value={draft.refusal?.comment ?? ''} onChange={(ev) => set('refusal', { ...draft.refusal, comment: ev.target.value })} /></Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, required, error, refEl }: {
  label: string; children: React.ReactNode;
  required?: boolean;   // показать звёздочку (обязательное поле, когда валидация применима)
  error?: boolean;      // подсветить красным (незаполненное обязательное после «Сохранить»)
  refEl?: (el: HTMLElement | null) => void;
}) {
  return (
    <label ref={refEl} className={cn('block', error && 'field-error')}>
      <span className="block text-[11px] text-ink-3 mb-0.5">
        {label}{required && <span className="text-status-refused ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

// Контрол «Приложить чек» — скрытый file-input + кнопка + список уже прикреплённых
// чеков с крестиком «удалить». Используется рядом с полями «Предоплата» и «Остаток».
function ReceiptControl({ receipts, onAdd, onRemove, dealId, keyPrefix, canRemove = true }: {
  receipts: PaymentReceipt[];
  onAdd: (files: FileList | null) => void;
  onRemove: (id: string) => void;
  /** Нужны, чтобы прикреплённый чек можно было ОТКРЫТЬ, а не только удалить. */
  dealId: string;
  keyPrefix: 'p' | 'r';
  /** Показывать ли крестик удаления. false — для финансиста в закрытой карточке (только догрузка). */
  canRemove?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="mt-1.5">
      <input ref={ref} type="file" accept="image/*,application/pdf" multiple className="hidden"
        onChange={(ev) => { onAdd(ev.target.files); ev.target.value = ''; }} />
      <button type="button" onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-line text-ink-2 text-xs hover:bg-surface-2 transition-colors">
        <Paperclip className="w-3.5 h-3.5" /> Приложить чек
      </button>
      {receipts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {receipts.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-line bg-surface-2 text-ink-2 text-xs max-w-full">
              <FileText className="w-3.5 h-3.5 flex-shrink-0" />
              {/* Имя файла — кликабельно: чек из Битрикса отдаём через свой прокси (он
                  работает и без доступа в CRM), свой — через attachment-view. Пока файл
                  ещё грузится в базу (нет ни тела, ни признака) — просто текст. */}
              {r.bitrix_url ? (
                <button type="button" onClick={() => openBitrixFileView(dealId, `${keyPrefix}:${r.id}`)}
                  title="Открыть чек" className="truncate text-accent hover:underline">{r.filename}</button>
              ) : (r.has_data || r.data_url) ? (
                <button type="button" onClick={() => openAttachmentView(dealId, `${keyPrefix}:${r.id}`)}
                  title="Открыть чек" className="truncate text-accent hover:underline">{r.filename}</button>
              ) : (
                <span className="truncate" title="Файл загружается…">{r.filename}</span>
              )}
              {canRemove && (
                <button type="button" onClick={() => onRemove(r.id)} title="Удалить чек" className="flex-shrink-0 text-ink-3 hover:text-red-500">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ВКЛАДКА «ПЕРСОНАЛ» ──────────────────────────────────
function PeopleView({ draft, employees, teams }: { draft: Deal; employees: { id: string; full_name: string; username?: string }[]; teams: TeamOpt[] }) {
  const staff = draft.staff_assignments ?? [];
  const staffIds = new Set(staff.map((s) => s.employee_id));
  const team = draft.team_id ? teams.find((t) => t.id === draft.team_id) : null;
  // Участники назначенной КОМАНДЫ (кого нет среди поимённых) — показываем тоже: раньше вкладка
  // выводила только поимённые назначения, и при игре с одной командой список выглядел пустым,
  // хотя в календаре/графике её люди видны (там состав = поимённые ∪ участники команды).
  const list: { employee_id: string; role: string; snapshot?: string }[] = [
    ...staff.map((a) => ({ employee_id: a.employee_id, role: roleLabel(a.role), snapshot: a.employee_name })),
    ...(team?.member_ids ?? []).filter((id) => !staffIds.has(id))
      .map((id) => ({ employee_id: id, role: `Команда «${team!.name}»` })),
  ];
  return (
    <div>
      <div className="grid grid-cols-[160px_1fr] text-xs text-ink-3 px-3 py-2 border-b border-line">
        <span>Роль</span><span>Сотрудник</span>
      </div>
      {list.length === 0 ? (
        <div className="px-3 py-8 text-center text-sm text-ink-3">Сотрудники не назначены.</div>
      ) : list.map((a, i) => {
        const emp = employees.find((m) => m.id === a.employee_id);
        // Имя берём из справочника; если карточки уже нет — показываем слепок, снятый при
        // назначении, и помечаем «удалён», чтобы было ясно, почему человека нет в персонале.
        const name = emp?.full_name ?? a.snapshot;
        return (
          <div key={i} className="grid grid-cols-[160px_1fr] items-center px-3 py-2.5 border-b border-line/60 text-sm">
            <span className="text-ink-2">{a.role}</span>
            <span className="flex items-center gap-1.5 text-ink-1">
              {name ?? 'неизвестно'}
              {emp?.username && <span className="text-ink-3">(@{emp.username})</span>}
              {!emp && name && <span className="text-ink-3">(удалён)</span>}
              <span className="text-warm-mint">✅</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PeopleEdit({ draft, setDraft, employees, teams }: {
  draft: Deal; setDraft: (d: Deal) => void;
  employees: { id: string; full_name: string; role?: string; is_active?: boolean; branch_id?: string | null }[];
  teams: TeamOpt[];
}) {
  const list = draft.staff_assignments ?? [];
  const [applyOpen, setApplyOpen] = useState(false);
  // Свои доплаты (03.11.2026, см. PayRatesPage.tsx) — только НАЗВАНИЯ, без сумм (сумма
  // остаётся под правом «Ставки»); тут их можно отметить конкретному человеку на игре.
  const [customAddons, setCustomAddons] = useState<CustomAddonLabel[]>([]);
  useEffect(() => { getCustomAddonLabels().then(setCustomAddons).catch(() => setCustomAddons([])); }, []);

  // Кого вообще можно поставить на игру (решения заказчика 28–29.08.2026):
  //
  // 1) ДОЛЖНОСТЬ. Только те, кто выходит на игру (STAGE_ROLES); офис — менеджеры ОП,
  //    руководство, SMM, кассиры — в состав смены не ставят.
  // 2) РОЛЬ СТРОКИ НЕ ОГРАНИЧИВАЕТ, а только сортирует: подходящие по должности сверху,
  //    остальные ниже с подписью должности. Раньше фильтр был жёстким (совпадение
  //    должности + управляющие), из-за чего ведущего нельзя было поставить соведущим:
  //    соведущих на сеть заведено шестеро против 37 ведущих, причём в Астане и Алматы —
  //    ни одного, и в списке оставались одни управляющие.
  // 3) ФИЛИАЛ. Только люди филиала игры. Проверено по боевым данным: из 696 назначений
  //    660 — свой филиал, а все «чужие» объясняются ивентами и одним переводом человека
  //    между городами. Людей между филиалами не гоняют, поэтому список режем.
  const stageEmployees = employees.filter((m) => m.is_active !== false && STAGE_ROLES.has(m.role ?? ''));
  // Филиал игры. У ИВЕНТА карточка лежит в служебном филиале «Ивент», где сотрудников нет
  // вообще, а работают люди города проведения — берём его из mirror_branch_id (тот же приём,
  // что и для списка команд, см. teamsForDeal).
  const allowedBranches = [draft.branch_id, draft.mirror_branch_id].filter(Boolean) as string[];
  const ofBranch = allowedBranches.length > 0
    ? stageEmployees.filter((m) => m.branch_id && allowedBranches.includes(m.branch_id))
    : stageEmployees;
  // Предохранитель от ПУСТОГО списка: у игры не проставлен филиал (таких в базе половина —
  // старый импорт из Битрикса, состава у них нет) или в филиале не заведено ни одного
  // «игрового» сотрудника. Тогда лучше показать всех, чем не дать выбрать никого.
  const pool = ofBranch.length > 0 ? ofBranch : stageEmployees;

  const optsFor = (roleSlot: string, selectedId: string) => {
    // «Ведущий 2» ищет среди тех же людей, что и «Ведущий 1» — своей должности
    // «Ведущий 2» у сотрудников не бывает, это просто вторая роль на КОНКРЕТНОЙ игре.
    const want = roleSlot === 'vedushiy2' ? 'Ведущий' : roleLabel(roleSlot);
    // Уже выбранный человек показывается ВСЕГДА — даже уволенный, «офисный» или переведённый
    // в другой город: иначе правка старой карточки молча стирала бы его из состава.
    const selected = selectedId && !pool.some((m) => m.id === selectedId)
      ? employees.filter((m) => m.id === selectedId)
      : [];
    return [...pool, ...selected]
      .sort((a, b) => (a.role === want ? 0 : 1) - (b.role === want ? 0 : 1) || a.full_name.localeCompare(b.full_name))
      .map((m) => ({ id: m.id, label: m.role && m.role !== want ? `${m.full_name} · ${m.role.toLowerCase()}` : m.full_name }));
  };
  const add = () => setDraft({ ...draft, staff_assignments: [...list, { employee_id: '', role: 'vedushiy' }] });
  const remove = (i: number) => setDraft({ ...draft, staff_assignments: list.filter((_, idx) => idx !== i) });
  // При выборе человека кладём рядом СЛЕПОК его имени (employee_name) — по тому же принципу,
  // что и состав команды: он копируется в игру и дальше не зависит от справочника. Если карточку
  // сотрудника потом удалят, в прошедшей игре останется имя, а не «неизвестно».
  const update = (i: number, k: 'employee_id' | 'role', v: string) => setDraft({
    ...draft,
    staff_assignments: list.map((a, idx) => {
      if (idx !== i) return a;
      if (k !== 'employee_id') return { ...a, role: v };
      return { ...a, employee_id: v, employee_name: v ? (employees.find((m) => m.id === v)?.full_name ?? a.employee_name) : undefined };
    }),
  });
  const toggleAddon = (i: number, key: string) => setDraft({
    ...draft,
    staff_assignments: list.map((a, idx) => {
      if (idx !== i) return a;
      const has = (a.addon_keys ?? []).includes(key);
      return { ...a, addon_keys: has ? a.addon_keys!.filter((k) => k !== key) : [...(a.addon_keys ?? []), key] };
    }),
  });
  return (
    <div className="space-y-3">
      {list.map((a, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <Field label="Роль сотрудника">
            <select className="input" value={a.role} onChange={(ev) => update(i, 'role', ev.target.value)}>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </Field>
          <Field label="Сотрудник">
            <Combobox
              value={a.employee_id || null}
              onChange={(id) => update(i, 'employee_id', id ?? '')}
              options={optsFor(a.role, a.employee_id)}
              placeholder="Начните вводить имя"
            />
          </Field>
          <button onClick={() => remove(i)} className="icon-btn !w-9 !h-9 mb-0.5 text-ink-3 hover:text-status-refused"><X className="w-4 h-4" /></button>
          {customAddons.length > 0 && (
            <div className="col-span-full flex flex-wrap gap-1.5 -mt-1">
              {customAddons.map((ca) => (
                <label key={ca.key} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-line cursor-pointer text-ink-2 hover:bg-surface-2">
                  <input type="checkbox" className="w-3 h-3 accent-accent" checked={(a.addon_keys ?? []).includes(ca.key)} onChange={() => toggleAddon(i, ca.key)} />
                  {ca.label}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="flex justify-end">
        <button onClick={add} className="btn-primary !h-9"><Plus className="w-4 h-4" /> Добавить сотрудника</button>
      </div>
      {list.length > 0 && (
        <div className="flex justify-end">
          <button onClick={() => setApplyOpen(true)} className="btn-soft !h-9">
            <Copy className="w-4 h-4" /> Применить состав к играм дня
          </button>
        </div>
      )}
      <div className="pt-2 border-t border-line">
        <Field label="Команда">
          <select className="input" value={draft.team_id ?? ''} onChange={(ev) => setDraft({ ...draft, team_id: ev.target.value || null })}>
            <option value="">— не назначена —</option>
            {teamsForDeal(teams, draft.branch_id, draft.team_id, draft.mirror_branch_id).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}{t.branch_id && t.branch_id !== draft.branch_id ? ` · ${branchName(t.branch_id)}` : ''}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <ApplyStaffToDayModal open={applyOpen} draft={draft} onClose={() => setApplyOpen(false)} />
    </div>
  );
}

/** Русское склонение числительного: plural(2, 'игру', 'игры', 'игр') → 'игры'. */
function pluralRu(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

/**
 * День игры с учётом часового пояса филиала — ТА ЖЕ логика, что группирует игры по дням
 * в календаре (CalendarPage: inBranchTz + tzShiftMs + startOfDay). Возвращает ключ дня
 * (локальная полночь) или null, если у игры нет ни start_at, ни event_date.
 */
function dealDayKey(
  d: Deal,
  branches: { id: string; tz_offset_minutes: number }[],
  useBranchTz: boolean,
  tzShiftMs: number,
): number | null {
  const ts = d.event.start_at ?? d.event.event_date;
  if (ts == null) return null;
  const localOff = -new Date().getTimezoneOffset();
  const branch = branches.find((b) => b.id === d.branch_id);
  const base = useBranchTz && branch ? inBranchTz(ts, branch.tz_offset_minutes, localOff) : ts;
  return startOfDay(base + tzShiftMs);
}

/**
 * Окно «Применить состав к играм дня»: показывает ДРУГИЕ игры того же дня и того же филиала,
 * даёт отметить нужные и проставить им НЕЗАВИСИМУЮ копию состава текущей игры (team_id не трогаем).
 */
function ApplyStaffToDayModal({ draft, open, onClose }: { draft: Deal; open: boolean; onClose: () => void }) {
  const deals = useStore((s) => s.deals);
  const branches = useStore((s) => s.branches);
  const useBranchTz = useStore((s) => s.settings.use_branch_tz_in_dashboards);
  const saveDeal = useStore((s) => s.saveDeal);
  const toast = useStore((s) => s.toast);
  const tzShiftMs = useTzShiftMs();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Сбрасываем галочки при каждом открытии — по умолчанию НИЧЕГО не отмечено.
  useEffect(() => { if (open) setChecked(new Set()); }, [open]);

  const dayKey = useMemo(() => dealDayKey(draft, branches, useBranchTz, tzShiftMs), [draft, branches, useBranchTz, tzShiftMs]);

  const targets = useMemo(() => {
    if (dayKey == null) return [];
    return deals
      .filter((d) => d.id !== draft.id
        && d.branch_id === draft.branch_id
        && dealDayKey(d, branches, useBranchTz, tzShiftMs) === dayKey)
      .sort((a, b) => (a.event.start_at ?? 0) - (b.event.start_at ?? 0));
  }, [deals, draft.id, draft.branch_id, dayKey, branches, useBranchTz, tzShiftMs]);

  const toggle = (id: string) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const apply = async () => {
    const picked = targets.filter((d) => checked.has(d.id));
    if (picked.length === 0) return;
    setBusy(true);
    try {
      for (const d of picked) {
        // ГЛУБОКАЯ КОПИЯ состава — новый массив из новых объектов, без общих ссылок,
        // чтобы правка одной игры не затрагивала другие. team_id НЕ копируем.
        const staff_assignments = (draft.staff_assignments ?? []).map((a) => ({ ...a }));
        await saveDeal({ ...d, staff_assignments });
      }
      const n = picked.length;
      toast({ kind: 'success', text: `Состав проставлен в ${n} ${pluralRu(n, 'игру', 'игры', 'игр')}` });
      onClose();
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось проставить состав' });
    } finally {
      setBusy(false);
    }
  };

  const title = `Применить состав к играм${dayKey != null ? ' ' + formatDateShort(dayKey) : ''}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={targets.length === 0 ? (
        <button onClick={onClose} className="btn-ghost">Закрыть</button>
      ) : (
        <>
          <button onClick={onClose} className="btn-ghost" disabled={busy}>Отмена</button>
          <button onClick={apply} className="btn-primary" disabled={busy || checked.size === 0}>
            Применить ({checked.size})
          </button>
        </>
      )}
    >
      {targets.length === 0 ? (
        <div className="py-8 text-center text-sm text-ink-3">На этот день других игр нет</div>
      ) : (
        <div className="space-y-1.5">
          {targets.map((d) => {
            const on = checked.has(d.id);
            const time = d.event.start_at != null ? formatHM(d.event.start_at) : '—';
            const place = d.event.place ? EVENT_PLACE_LABEL[d.event.place] : (d.event.address || '');
            const existing = (d.staff_assignments ?? []).length;
            return (
              <label key={d.id}
                className={cn('flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors',
                  on ? 'border-accent bg-accent/5' : 'border-line hover:bg-surface-2')}>
                <input type="checkbox" className="mt-0.5 w-4 h-4 flex-shrink-0" checked={on} onChange={() => toggle(d.id)} />
                <span className="min-w-0 flex-1 text-sm">
                  <span className="text-ink-1">
                    <span className="font-semibold">{time}</span>
                    <span className="text-ink-3"> · </span>{dealTitle(d)}
                    {place && <><span className="text-ink-3"> · </span><span className="text-ink-2">{place}</span></>}
                  </span>
                  {existing > 0 && (
                    <span className="block text-xs text-status-refused mt-0.5">
                      ⚠ уже есть состав ({existing} {pluralRu(existing, 'чел.', 'чел.', 'чел.')})
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ── ВКЛАДКА «КОММЕНТАРИИ» — лента сообщений (чат) с фильтром по тегам ──
const DOC_FILTERS: DocCategory[] = ['photo', 'blank', 'certificate', 'payment'];
type FeedMsg = NonNullable<Deal['comments_feed']>[number];

// Пороги вложений комментария. Снимок чека должен читаться, поэтому обычные фото и скрины
// кладём КАК ЕСТЬ. Пережимаем только по-настоящему тяжёлые файлы (лимит тела запроса на
// сервере — 25 МБ), причём мягко: 2400px по длинной стороне и высокое качество JPEG.
const ATTACH_KEEP_AS_IS_BYTES = 4 * 1024 * 1024;   // ≤ 4 МБ — не трогаем
const ATTACH_MAX_STORED_BYTES = 8 * 1024 * 1024;   // потолок для пережатой картинки
const ATTACH_MAX_SIDE = 2400;                      // длинная сторона (меньше — не уменьшаем)
const ATTACH_QUALITY_STEPS = [0.92, 0.88, 0.85, 0.82];

/** Примерный вес data-URL в байтах (base64 → ~3/4 длины). */
function dataUrlBytes(url: string): number {
  const b64 = url.slice(url.indexOf(',') + 1);
  return Math.round((b64.length * 3) / 4);
}

/**
 * Файл → data-URL для вложения комментария (плюс итоговый mime — он меняется при пережатии).
 * Картинку до 4 МБ и любой не-image (PDF) берём как есть — иначе сфотографированный чек
 * становится нечитаемым. Крупнее — ужимаем до 2400px по длинной стороне (не увеличивая
 * маленькие), JPEG 0.92; если всё ещё тяжело — снижаем качество ступенями до 0.82.
 */
async function fileToCompressedDataUrl(file: File): Promise<{ data_url: string; mime: string }> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(new Error('Не удалось прочитать файл'));
    r.readAsDataURL(file);
  });
  const asIs = { data_url: dataUrl, mime: file.type || 'application/octet-stream' };
  if (!file.type.startsWith('image/') || file.size <= ATTACH_KEEP_AS_IS_BYTES) return asIs;
  return await new Promise<{ data_url: string; mime: string }>((res) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, ATTACH_MAX_SIDE / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { res(asIs); return; }
      ctx.drawImage(img, 0, 0, w, h);
      let out = canvas.toDataURL('image/jpeg', ATTACH_QUALITY_STEPS[0]);
      for (let i = 1; i < ATTACH_QUALITY_STEPS.length && dataUrlBytes(out) > ATTACH_MAX_STORED_BYTES; i++) {
        out = canvas.toDataURL('image/jpeg', ATTACH_QUALITY_STEPS[i]);
      }
      res({ data_url: out, mime: 'image/jpeg' });
    };
    img.onerror = () => res(asIs);
    img.src = dataUrl;
  });
}

function CommentsTab({ draft, setDraft, canDelete }: { draft: Deal; setDraft: (d: Deal) => void; canDelete: boolean }) {
  const [filter, setFilter] = useState<DocCategory | null>(null);
  const [text, setText] = useState('');
  const [pendingCat, setPendingCat] = useState<DocCategory | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const author = useStore.getState().bitrixUser ?? useStore.getState().currentUser?.full_name ?? 'Вы';
  const authorId = useStore((s) => s.currentUser?.employee_id) ?? null;
  const myRole = useStore((s) => s.currentUser?.access_role);
  const canDeleteAny = usePerms().deals.delete;
  const saveDeal = useStore((s) => s.saveDeal);
  const toast = useStore((s) => s.toast);

  const feed = draft.comments_feed ?? [];
  const shown = (filter ? feed.filter((m) => m.category === filter) : feed)
    .slice().sort((a, b) => a.created_at - b.created_at);

  // Кто может удалить сообщение: автор — своё, руководитель/управляющий (и любой с правом
  // «удалять сделки») — любое. Сверх этого действует общий замок canDelete (правило «сутки
  // после закрытия»). У старых сообщений author_id не заполнялся — сверяем по имени автора.
  const isBoss = myRole === 'owner' || myRole === 'branch_manager' || canDeleteAny;
  const isMine = (m: FeedMsg) => (m.author_id != null && authorId != null)
    ? m.author_id === authorId
    : (m.author ?? '').trim().toLowerCase() === author.trim().toLowerCase();
  const canDeleteMsg = (m: FeedMsg) => canDelete && (isBoss || isMine(m));

  // Фото ленты (в текущем порядке показа) — по ним листает лайтбокс.
  const photos = shown.filter((m) => m.attachment?.data_url && m.attachment.mime.startsWith('image/'))
    .map((m) => ({ data_url: m.attachment!.data_url!, filename: m.attachment!.filename }));

  // Комментарии/вложения сохраняем СРАЗУ на сервер (не дожидаясь «Сохранить») — иначе
  // запись с телефона не попадёт в базу и её не увидят на компьютере. При сбое — сообщаем
  // (раньше ошибка глоталась молча и запись пропадала при выходе из карточки).
  const persist = (next: Deal) => {
    const prev = draft; // версия ДО правки — вернём её, если сохранение не пройдёт
    setDraft(next);
    saveDeal(next).catch(() => {
      // ОТКАТ: не оставляем неотправленный комментарий/фото висеть в карточке (иначе кажется,
      // что сохранилось, а после переоткрытия его нет — это и есть жалоба про «пропавшие файлы»).
      // Сообщение об ошибке показывает сам saveDeal (в store есть error-тост) — здесь НЕ дублируем.
      setDraft(prev);
    });
  };

  const pushMsg = (msg: Omit<FeedMsg, 'id' | 'created_at' | 'author'>) => {
    // author_id пишем у новых сообщений — по нему сервер понимает, чьё сообщение (имя автора
    // ненадёжно: тёзки, смена ФИО).
    const full: FeedMsg = { id: nanoid(), created_at: Date.now(), author, author_id: authorId ?? undefined, ...msg };
    persist({ ...draft, comments_feed: [...feed, full] });
  };
  const send = () => { if (!text.trim()) return; pushMsg({ text: text.trim() }); setText(''); };
  // Автовысота поля комментария: растёт под текст (Enter = новый абзац), но не выше ~6 строк.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [text]);
  const addFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const f of Array.from(files)) {
      const { data_url, mime } = await fileToCompressedDataUrl(f);
      pushMsg({ attachment: { filename: f.name, mime, data_url }, category: pendingCat ?? filter ?? 'photo' });
    }
    setPendingCat(null);
  };
  const removeTag = (id: string) => persist({ ...draft, comments_feed: feed.map((m) => m.id === id ? { ...m, category: undefined } : m) });
  // Удаление сообщения: вложение уходит вместе с ним (оно лежит внутри сообщения).
  const removeMsg = (id: string) => {
    if (!confirm('Удалить комментарий?')) return;
    persist({ ...draft, comments_feed: feed.filter((m) => m.id !== id) });
  };
  const fmtTs = (ts: number) => new Date(ts).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex flex-col h-full min-h-[320px]">
      {/* фильтры по тегам */}
      <div className="flex items-center gap-2 flex-wrap pb-3 flex-shrink-0">
        {DOC_FILTERS.map((c) => {
          const active = filter === c;
          const cnt = feed.filter((m) => m.category === c).length;
          return (
            <button key={c} onClick={() => setFilter(active ? null : c)}
              className={cn('inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-semibold transition-colors',
                active ? 'bg-sky-500 text-white border-sky-500' : 'border-sky-400/50 text-sky-600 hover:bg-sky-50',
                cnt === 0 && !active && 'opacity-50 line-through')}>
              📋 {DOC_CATEGORY_LABEL[c]}
            </button>
          );
        })}
        {filter && <button onClick={() => setFilter(null)} className="ml-auto inline-flex items-center gap-1 px-3 h-8 rounded-full border border-line text-xs text-ink-2 hover:bg-surface-2"><X className="w-3.5 h-3.5" /> Сброс</button>}
      </div>

      {/* лента сообщений */}
      <div className="flex-1 space-y-4 overflow-y-auto">
        {shown.length === 0 ? (
          <div className="text-center py-10 text-sm text-ink-3">Отсутствуют</div>
        ) : shown.map((m) => (
          <div key={m.id} className="space-y-1.5 group">
            <div className="flex items-center gap-2">
              <Avatar name={m.author ?? '—'} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink-1 truncate">{m.author ?? '—'}</div>
                <div className="text-xs text-ink-3 tabular-nums">{fmtTs(m.created_at)}</div>
              </div>
              {canDeleteMsg(m) && (
                <button onClick={() => removeMsg(m.id)} title="Удалить комментарий"
                  className="icon-btn !w-7 !h-7 text-ink-3 hover:text-status-refused opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              {/* Галочка «прочитано» убрана по просьбе заказчика: категория (Фото/Бланк/
                  Сертификат/Оплата) у нас задаётся иначе, эта отметка лишняя. Код оставлен
                  закомментированным на случай возврата.
              <Check className={cn('w-4 h-4', m.read ? 'text-warm-mint' : 'text-ink-3/50')} /> */}
            </div>
            {m.text && <div className="ml-11 text-sm text-ink-1 whitespace-pre-wrap leading-relaxed">{m.text}</div>}
            {/* Тело вложения догружается фоном (в списке сделок его нет). Пока не приехало —
                на его месте серый прямоугольник примерно того же размера: место в ленте занято,
                видно, что картинка грузится. Догрузилось — тихо подставилось. */}
            {m.attachment && (m.attachment.mime.startsWith('image/') ? (
              m.attachment.data_url ? (
                <img src={m.attachment.data_url} alt={m.attachment.filename}
                  onClick={() => setLightbox(photos.findIndex((p) => p.data_url === m.attachment!.data_url))}
                  className="ml-11 max-w-[300px] rounded-lg border border-line cursor-zoom-in" />
              ) : (
                <div className="ml-11 w-[300px] h-[200px] rounded-lg border border-line bg-surface-2 animate-pulse" />
              )
            ) : (m.attachment.has_data || m.attachment.data_url) && m.id != null ? (
              // Не-картинка (напр. PDF): открываем по http-адресу сервера (attachment-view),
              // чтобы браузер показал файл inline, а не срывался на скачивание.
              <a href="#" onClick={(e) => { e.preventDefault(); openAttachmentView(draft.id, `c:${m.id}`); }} className="ml-11 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-line text-accent text-xs hover:bg-surface-2"><FileText className="w-3.5 h-3.5" /> {m.attachment.filename}</a>
            ) : (
              <span className="ml-11 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-line bg-surface-2 text-ink-3 text-xs animate-pulse" title="Файл загружается…"><FileText className="w-3.5 h-3.5" /> {m.attachment.filename}</span>
            ))}
            {m.category && (
              <div className="ml-11">
                <span className="inline-flex items-center gap-1 pl-3 pr-2 h-7 rounded-full bg-sky-500 text-white text-xs font-semibold">
                  📋 {DOC_CATEGORY_LABEL[m.category]}
                  <button onClick={() => removeTag(m.id)} className="ml-0.5 w-4 h-4 rounded-full hover:bg-white/20 grid place-items-center"><X className="w-3 h-3" /></button>
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ввод сообщения */}
      <div className="flex items-end gap-2 pt-3 mt-3 border-t border-line flex-shrink-0">
        <button onClick={() => fileRef.current?.click()} className="icon-btn !w-9 !h-9 text-ink-3 flex-shrink-0" title="Прикрепить файл"><Paperclip className="w-4 h-4" /></button>
        <input ref={fileRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(ev) => addFiles(ev.target.files)} />
        {/* Enter = новый абзац, Shift+Enter = отправить (по просьбе заказчика). Кнопка-стрелка тоже шлёт. */}
        <textarea ref={taRef} rows={1} className="input flex-1 resize-none py-2 leading-relaxed" placeholder="Добавить комментарий (Enter — новый абзац, Shift+Enter — отправить)"
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); send(); } }} />
        <button onClick={send} className="icon-btn !w-9 !h-9 text-accent flex-shrink-0" title="Отправить"><Send className="w-4 h-4" /></button>
      </div>

      {lightbox != null && lightbox >= 0 && photos[lightbox] && (
        <PhotoLightbox items={photos} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

/**
 * Полноэкранный просмотр фото из ленты комментариев. Закрытие — Escape, клик по фону,
 * «крестик». Стрелки (и ←/→) появляются, только когда фото в ленте больше одного.
 * Рисуем через портал в body: карточка сделки может лежать внутри контейнера с фильтром/
 * трансформацией, а он ломает position:fixed.
 */
const LIGHTBOX_MIN_ZOOM = 1;
const LIGHTBOX_MAX_ZOOM = 5;
const clampZoom = (z: number) => Math.min(LIGHTBOX_MAX_ZOOM, Math.max(LIGHTBOX_MIN_ZOOM, z));

function PhotoLightbox({ items, index, onIndex, onClose }: {
  items: { data_url: string; filename: string }[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const many = items.length > 1;
  const go = (step: number) => onIndex((index + step + items.length) % items.length);

  // Поворот (0/90/180/270) и зум/сдвиг картинки. Трансформация — ТОЛЬКО на <img>, не на
  // обёртке fixed inset-0 (см. комментарий у объявления функции): transform на родителе
  // ломает position:fixed у детей.
  const [rot, setRot] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [interacting, setInteracting] = useState(false); // во время драга/пинча — без transition

  // Сброс поворота/зума/сдвига при смене фото (и при открытии — эффект стартует со
  // значений по умолчанию при первом монтировании тоже).
  useEffect(() => {
    setRot(0); setZoom(1); setPan({ x: 0, y: 0 });
  }, [index]);

  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  const dist2 = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y);

  const onPointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = Array.from(pointersRef.current.values());
      pinchRef.current = { dist: dist2(a, b), zoom };
      dragRef.current = null;
      setInteracting(true);
    } else if (pointersRef.current.size === 1) {
      dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
      setInteracting(true);
    }
  };
  const onPointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = Array.from(pointersRef.current.values());
      const nd = dist2(a, b);
      if (pinchRef.current.dist > 0) {
        setZoom(clampZoom(pinchRef.current.zoom * (nd / pinchRef.current.dist)));
      }
    } else if (pointersRef.current.size === 1 && dragRef.current && zoom > 1) {
      const dx = e.clientX - dragRef.current.x;
      const dy = e.clientY - dragRef.current.y;
      setPan({ x: dragRef.current.panX + dx, y: dragRef.current.panY + dy });
    }
  };
  const endPointer = (e: React.PointerEvent<HTMLImageElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size < 1) { dragRef.current = null; setInteracting(false); }
    else if (pointersRef.current.size === 1) {
      // Осталась одна точка после щипка — начать одиночный драг заново.
      const [p] = Array.from(pointersRef.current.values());
      dragRef.current = { x: p.x, y: p.y, panX: pan.x, panY: pan.y };
    }
  };

  const onWheel = (e: React.WheelEvent<HTMLImageElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => {
      const nz = clampZoom(z * factor);
      if (nz === 1) setPan({ x: 0, y: 0 });
      return nz;
    });
  };

  const onDoubleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    setZoom((z) => {
      const nz = z > 1 ? 1 : 2;
      if (nz === 1) setPan({ x: 0, y: 0 });
      return nz;
    });
  };

  const rotateBy = (delta: number) => setRot((r) => (r + delta + 360) % 360);
  const zoomBy = (factor: number) => setZoom((z) => {
    const nz = clampZoom(z * factor);
    if (nz === 1) setPan({ x: 0, y: 0 });
    return nz;
  });

  useEffect(() => {
    setLightboxOpen(true);   // пока флаг поднят, Escape не закрывает карточку сделки
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      if (many && e.key === 'ArrowLeft') onIndex((index - 1 + items.length) % items.length);
      if (many && e.key === 'ArrowRight') onIndex((index + 1) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => { setLightboxOpen(false); window.removeEventListener('keydown', onKey); };
  }, [index, items.length, many, onClose, onIndex]);

  const cur = items[index];
  // При повороте на 90°/270° «ширина» картинки становится её визуальной высотой, поэтому
  // ограничения по ширине/высоте нужно поменять местами — иначе широкое фото после
  // поворота не влезает и обрезается сверху/снизу. Так оно просто подстраивается под экран.
  const rotated = rot % 180 !== 0;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/85 animate-in" onClick={onClose}>
      <img
        src={cur.data_url} alt={cur.filename}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={onDoubleClick}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={endPointer}
        style={{
          maxWidth: rotated ? '88vh' : '92vw',
          maxHeight: rotated ? '92vw' : '88vh',
          transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rot}deg) scale(${zoom})`,
          transition: interacting ? 'none' : 'transform 150ms ease-out',
          touchAction: 'none',
          // -webkit-user-drag: браузер иначе «уносит» само изображение вместо сдвига.
          WebkitUserDrag: 'none',
          userSelect: 'none',
          cursor: zoom > 1 ? (interacting ? 'grabbing' : 'grab') : 'default',
        } as React.CSSProperties}
        className="object-contain rounded-lg shadow-pop select-none" />

      <div className="absolute top-3 right-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => rotateBy(-90)} title="Повернуть влево"
          className="icon-btn !w-9 !h-9 bg-white/10 text-white hover:bg-white/20"><RotateCcw className="w-4 h-4" /></button>
        <button onClick={() => rotateBy(90)} title="Повернуть вправо"
          className="icon-btn !w-9 !h-9 bg-white/10 text-white hover:bg-white/20"><RotateCw className="w-4 h-4" /></button>
        <button onClick={() => zoomBy(1 / 1.4)} title="Уменьшить" disabled={zoom <= LIGHTBOX_MIN_ZOOM}
          className="icon-btn !w-9 !h-9 bg-white/10 text-white hover:bg-white/20 disabled:opacity-40"><ZoomOut className="w-4 h-4" /></button>
        <button onClick={() => zoomBy(1.4)} title="Увеличить" disabled={zoom >= LIGHTBOX_MAX_ZOOM}
          className="icon-btn !w-9 !h-9 bg-white/10 text-white hover:bg-white/20 disabled:opacity-40"><ZoomIn className="w-4 h-4" /></button>
        <a href={cur.data_url} download={cur.filename} title="Скачать"
          className="icon-btn !w-9 !h-9 bg-white/10 text-white hover:bg-white/20"><Download className="w-4 h-4" /></a>
        <button onClick={onClose} title="Закрыть"
          className="icon-btn !w-9 !h-9 bg-white/10 text-white hover:bg-white/20"><X className="w-4 h-4" /></button>
      </div>

      {many && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} title="Предыдущее"
            className="absolute left-3 icon-btn !w-10 !h-10 bg-white/10 text-white hover:bg-white/20"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} title="Следующее"
            className="absolute right-3 icon-btn !w-10 !h-10 bg-white/10 text-white hover:bg-white/20"><ChevronRight className="w-5 h-5" /></button>
          <div className="absolute bottom-4 text-xs text-white/80 tabular-nums">{index + 1} / {items.length}</div>
        </>
      )}
    </div>,
    document.body,
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <span className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold text-white flex-shrink-0"
      style={{ backgroundColor: `hsl(${hashHue(name)}, 55%, 48%)` }}>
      {initialsOf(name)}
    </span>
  );
}

// Считаем изменения сделки (старое→новое) для записи в историю.
type Ref = { id: string; name: string };
function buildChanges(prev: Deal, next: Deal, refs: { teams: Ref[]; game_formats: Ref[]; sources: Ref[]; occasions: Ref[] }, tzShiftMs: number): NonNullable<Deal['history'][number]['changes']> {
  const out: NonNullable<Deal['history'][number]['changes']> = [];
  const nm = (list: Ref[], id?: string | null) => (id ? list.find((x) => x.id === id)?.name : undefined);
  const titleOf = (d: Deal) => dealTitle(d);

  const cmp = (label: string, from?: string | number, to?: string | number, kind?: 'status') => {
    const f = from == null || from === '' ? undefined : String(from);
    const t = to == null || to === '' ? undefined : String(to);
    if (f !== t) out.push({ label, from: f, to: t, kind });
  };

  cmp('Название', titleOf(prev), titleOf(next));
  if ((prev.funnel_status ?? DEAL_TO_FUNNEL[prev.status]) !== (next.funnel_status ?? DEAL_TO_FUNNEL[next.status])) {
    out.push({ label: 'Статус', kind: 'status',
      from: FUNNEL_BY_KEY.get(prev.funnel_status ?? DEAL_TO_FUNNEL[prev.status])?.label,
      to: FUNNEL_BY_KEY.get(next.funnel_status ?? DEAL_TO_FUNNEL[next.status])?.label });
  }
  cmp('Календарь', nm(refs.teams, prev.team_id), nm(refs.teams, next.team_id));
  // Время игры (начало–конец) в истории раньше не фиксировалось — ни ручная правка,
  // ни перенос мышкой не оставляли следа. Пишем в поясе показа портала, как в календаре.
  cmp('Время игры',
    formatDateTimeRange(prev.event.start_at, prev.event.end_at, tzShiftMs),
    formatDateTimeRange(next.event.start_at, next.event.end_at, tzShiftMs));
  cmp('Общая сумма', prev.amount, next.amount);
  cmp('Имя гостя', prev.contact_name, next.contact_name);
  cmp('Телефон гостя', prev.contact_phone, next.contact_phone);
  cmp('Откуда узнали', nm(refs.sources, prev.source_id), nm(refs.sources, next.source_id));
  const pe = prev.event, ne = next.event;
  cmp('Комментарий', pe.comments, ne.comments);
  cmp('Адрес', pe.address, ne.address);
  cmp('Место проведения', pe.place === 'outside' ? 'Выезд' : 'Студия', ne.place === 'outside' ? 'Выезд' : 'Студия');
  cmp('Формат игры', nm(refs.game_formats, pe.game_format_id), nm(refs.game_formats, ne.game_format_id));
  cmp('Язык', pe.language, ne.language);
  cmp('Предоплата', pe.prepayment_note, ne.prepayment_note);
  cmp('Возраст участников', pe.participant_age_text, ne.participant_age_text);
  cmp('Кол-во участников', pe.participant_count_text, ne.participant_count_text);
  cmp('Повод', pe.occasion_details, ne.occasion_details);
  cmp('Доплата за дальность', pe.extra_distance_fee, ne.extra_distance_fee);
  cmp('Длительность игры', pe.game_duration_minutes, ne.game_duration_minutes);
  cmp('Играли ранее', pe.played_before ? 'да' : 'нет', ne.played_before ? 'да' : 'нет');
  cmp('Расчётный счёт', pe.uses_invoice_account ? 'да' : 'нет', ne.uses_invoice_account ? 'да' : 'нет');
  cmp('Ожидание', pe.waiting_minutes ? fmtMinutesHM(pe.waiting_minutes) : '—', ne.waiting_minutes ? fmtMinutesHM(ne.waiting_minutes) : '—');
  cmp('Английское ведение', pe.english_hosting ? 'да' : 'нет', ne.english_hosting ? 'да' : 'нет');
  return out;
}

// ── ВКЛАДКА «ИСТОРИЯ» — таймлайн изменений ──────────────
function statusChip(label?: string) {
  const f = FUNNEL_STATUS.find((s) => s.label === label);
  return (
    <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded text-[11px] font-medium" style={f ? { backgroundColor: f.color + '33', color: '#444' } : undefined}>
      {f && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: f.color }} />}{label}
    </span>
  );
}
function HistoryTab({ draft, tzShiftMs }: { draft: Deal; tzShiftMs: number }) {
  const list = (draft.history ?? []).slice().reverse();
  if (list.length === 0) return <div className="text-center py-10 text-sm text-ink-3 italic">История пуста.</div>;
  return (
    <div className="space-y-1">
      {list.map((h, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center pt-1">
            <span className="w-3 h-3 rounded-full bg-accent flex-shrink-0" />
            {i < list.length - 1 && <span className="flex-1 w-px bg-line" />}
          </div>
          <div className="flex-1 pb-4 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium text-ink-1">{h.action}</div>
              <div className="text-xs text-ink-3 tabular-nums flex-shrink-0">{new Date(h.ts + tzShiftMs).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            {h.changes && h.changes.length > 0 && (
              <div className="mt-1 space-y-0.5 text-sm">
                {h.changes.map((c, j) => (
                  <div key={j} className="text-ink-2 leading-snug">
                    {c.from == null ? (
                      <span><span className="text-ink-3">Указан параметр</span> <span className="font-semibold text-ink-1">{c.label}</span>: {c.kind === 'status' ? statusChip(c.to) : c.to}</span>
                    ) : (
                      <span><span className="text-ink-3">Изменено</span> <span className="font-semibold text-ink-1">{c.label}</span>:{' '}
                        {c.kind === 'status' ? statusChip(c.from) : <span className="line-through text-ink-3">{c.from}</span>}
                        {' → '}
                        {c.kind === 'status' ? statusChip(c.to) : <span className="text-ink-1">{c.to}</span>}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {h.user && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="w-5 h-5 rounded-full grid place-items-center text-[8px] font-bold text-white" style={{ backgroundColor: `hsl(${hashHue(h.user)}, 55%, 48%)` }}>{initialsOf(h.user)}</span>
                <span className="text-xs text-ink-3">{h.user}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Один вопрос анкеты ОКК. ВЫНЕСЕН на верхний уровень (а не внутрь OkkTab) — иначе
// компонент пересоздаётся на каждый символ и поле теряет фокус после первого ввода.
function OkkQBlock({ q, num, value, onChange }: {
  q: OkkQuestion; num: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm text-ink-2"><span className="text-ink-3">{num}.</span> {q.q}</div>
      {q.type === 'radio' ? (
        <div className="space-y-1.5">
          {q.options!.map((opt) => (
            <label key={opt} className="flex items-center gap-2.5 text-sm text-ink-1 cursor-pointer">
              <input type="radio" name={q.id} checked={value === opt} onChange={() => onChange(opt)} className="w-4 h-4 accent-accent" />
              {opt}
            </label>
          ))}
        </div>
      ) : q.type === 'rating' ? (
        <select className="input !w-28" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      ) : (
        <input className="input" placeholder="Ваш ответ" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      )}
      {q.hint && <div className="bg-surface-2 border-l-2 border-line px-3 py-2 text-xs text-ink-2">{q.hint}</div>}
    </div>
  );
}

// ── ВКЛАДКА «ОКК» (опрос качества после мероприятия) ────
function OkkTab({ draft, setDraft, editable, fmtDateTime }: {
  draft: Deal; setDraft: (d: Deal) => void; editable: boolean; fmtDateTime: (ts: number) => string;
}) {
  // Вопросы анкеты — из настроек (руководитель мог их отредактировать), иначе дефолт-хардкод.
  const { required, optional, instructions } = okkConfig(useStore((s) => s.settings.okk_questions));
  const answers = draft.okk?.answers ?? {};
  const setAns = (qid: string, v: string) => {
    const prev = draft.okk ?? { answers: {} };
    setDraft({ ...draft, okk: { ...prev, answers: { ...prev.answers, [qid]: v }, updated_at: Date.now() } });
  };

  // ── Просмотр: таблица вопрос | ответ ──
  if (!editable) {
    const QaTable = ({ qs }: { qs: OkkQuestion[] }) => (
      <div className="border border-line rounded-lg overflow-hidden">
        {qs.map((q, i) => (
          <div key={q.id} className={cn('grid grid-cols-[1fr_120px] gap-3 px-3 py-2.5 text-sm', i % 2 === 0 ? 'bg-surface-1' : 'bg-surface-2/40')}>
            <span className="text-ink-2 leading-snug">{q.q}</span>
            <span className="text-ink-1 font-medium text-right break-words">{answers[q.id] || '—'}</span>
          </div>
        ))}
      </div>
    );
    return (
      <div className="space-y-4">
        {draft.okk && (
          <div className="text-xs text-ink-3 space-y-0.5">
            {draft.okk.created_at && <div><span className="font-semibold text-ink-2">Создано:</span> {draft.okk.created_by ?? '—'} ({fmtDateTime(draft.okk.created_at)})</div>}
            {draft.okk.updated_at && <div><span className="font-semibold text-ink-2">Изменено:</span> {draft.okk.updated_by ?? '—'} ({fmtDateTime(draft.okk.updated_at)})</div>}
          </div>
        )}
        <div>
          <div className="text-center text-sm font-bold text-ink-2 mb-2">Обязательные вопросы</div>
          <QaTable qs={required} />
        </div>
        <div>
          <div className="text-center text-sm font-bold text-ink-2 mb-2">Необязательные вопросы</div>
          <QaTable qs={optional} />
        </div>
      </div>
    );
  }

  // ── Редактирование: анкета с радио/текстом/оценкой ──
  return (
    <div className="space-y-4">
      {instructions.map((t, i) => (
        <div key={i} className="bg-surface-2 border-l-2 border-line px-3 py-2.5 text-xs text-ink-2 whitespace-pre-wrap leading-relaxed rounded-r">{t}</div>
      ))}

      <div className="text-sm font-bold text-ink-1 pt-1">1. Обязательные вопросы</div>
      <div className="space-y-5">
        {required.map((q, i) => (
          <OkkQBlock key={q.id} q={q} num={`1.${i + 1}`} value={answers[q.id] ?? ''} onChange={(v) => setAns(q.id, v)} />
        ))}
      </div>

      <div className="text-sm font-bold text-ink-1 pt-2">2. Необязательные вопросы</div>
      <div className="space-y-5">
        {optional.map((q, i) => (
          <OkkQBlock key={q.id} q={q} num={`2.${i + 1}`} value={answers[q.id] ?? ''} onChange={(v) => setAns(q.id, v)} />
        ))}
      </div>
    </div>
  );
}

